import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  Card,
  CardContent,
  TextField,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  FormControlLabel,
  Checkbox,
  LinearProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Undo as UndoIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  People as PeopleIcon,
  DeleteSweep as DeleteSweepIcon,
} from "@mui/icons-material";
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  updateDoc,
  writeBatch,
  getDoc,
} from "firebase/firestore";
import { DATABASE } from "../../../../Config/firebase";
import Swal from "sweetalert2";
import dayjs from "dayjs";

const MonthlyClosureRollback = () => {
  const [monthlyClosures, setMonthlyClosures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rollbackDialogOpen, setRollbackDialogOpen] = useState(false);
  const [selectedClosure, setSelectedClosure] = useState(null);
  const [rollbackProgress, setRollbackProgress] = useState(0);
  const [searchEmail, setSearchEmail] = useState("");
  const [searchMonth, setSearchMonth] = useState("");
  
  // Batch rollback states
  const [rollbackMode, setRollbackMode] = useState("single"); // "single", "batch", or "full"
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [selectedBatchMonth, setSelectedBatchMonth] = useState("");
  const [selectedBatchYear, setSelectedBatchYear] = useState("");
  const [deleteYearlyGraph, setDeleteYearlyGraph] = useState(false);
  const [batchPreviewUsers, setBatchPreviewUsers] = useState([]);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });
  const [batchResults, setBatchResults] = useState({ success: [], failed: [] });
  
  // Full rollback states (handles orphaned data)
  const [fullRollbackDialogOpen, setFullRollbackDialogOpen] = useState(false);
  const [selectedFullRollbackMonth, setSelectedFullRollbackMonth] = useState("");
  const [selectedFullRollbackYear, setSelectedFullRollbackYear] = useState("");
  const [fullRollbackDeleteYearlyGraph, setFullRollbackDeleteYearlyGraph] = useState(false);
  const [fullRollbackPreview, setFullRollbackPreview] = useState({
    monthly_grpahs: 0,
    ledgers: 0,
    transection_requests: 0,
    monthly_closings: 0,
    usersToRestore: 0
  });
  const [fullRollbackProgress, setFullRollbackProgress] = useState(0);
  const [fullRollbackUserDetails, setFullRollbackUserDetails] = useState([]);

  // Adicione este estado
  const [orphanRollbackDialogOpen, setOrphanRollbackDialogOpen] = useState(false);
  const [orphanMonth, setOrphanMonth] = useState("");
  const [orphanYear, setOrphanYear] = useState("");
  const [orphanGraphs, setOrphanGraphs] = useState([]);
  const [orphanDeleteYearlyGraph, setOrphanDeleteYearlyGraph] = useState(false);
  const [orphanDataCounts, setOrphanDataCounts] = useState({
    ledgers: 0,
    transactions: 0,
    monthly_closings: 0
  });

  useEffect(() => {
    loadMonthlyClosures();
  }, []);

  const loadMonthlyClosures = async () => {
    setLoading(true);
    try {
      // Get monthly closures data
      const monthlyClosingsQuery = query(
        collection(DATABASE, "monthly_closings"),
        orderBy("date", "desc")
      );
      const monthlyClosingsSnapshot = await getDocs(monthlyClosingsQuery);
      
      const closuresData = await Promise.all(
        monthlyClosingsSnapshot.docs.map(async (doc) => {
          const data = doc.data();
          
          // Get user information
          let userEmail = "Unknown";
          let userName = "Unknown";
          if (data.user_id) {
            try {
              const userDoc = await getDocs(
                query(collection(DATABASE, "users"), where("__name__", "==", data.user_id))
              );
              if (!userDoc.empty) {
                const userData = userDoc.docs[0].data();
                userEmail = userData.email || "Unknown";
                userName = userData.full_name || "Unknown";
              }
            } catch (error) {
              console.error("Error fetching user data:", error);
            }
          }

          return {
            id: doc.id,
            ...data,
            userEmail,
            userName,
            created_at: data.date?.toDate?.() || new Date(),
          };
        })
      );

      setMonthlyClosures(closuresData);
    } catch (error) {
      console.error("Error loading monthly closures:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load monthly closures data",
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredClosures = monthlyClosures.filter((closure) => {
    const emailMatch = !searchEmail || 
      closure.userEmail?.toLowerCase().includes(searchEmail.toLowerCase());
    const monthMatch = !searchMonth || 
      (closure.closing_month !== undefined && closure.closing_year !== undefined && 
       dayjs().month(closure.closing_month).year(closure.closing_year).format("MMMM YYYY").toLowerCase().includes(searchMonth.toLowerCase()));
    return emailMatch && monthMatch;
  });

  const handleRollbackClosure = (closure) => {
    setSelectedClosure(closure);
    setRollbackDialogOpen(true);
  };

  const confirmRollback = async () => {
    if (!selectedClosure) return;

    setRollbackProgress(0);
    try {
      const batch = writeBatch(DATABASE);
      const rollbackSteps = [];

      // Step 1: Find and delete monthly_grpahs entry
      setRollbackProgress(10);
      const monthlyGraphsQuery = query(
        collection(DATABASE, "monthly_grpahs"),
        where("user_id", "==", selectedClosure.user_id),
        where("graph_month", "==", selectedClosure.closing_month),
        where("graph_year", "==", selectedClosure.closing_year)
      );
      const monthlyGraphsSnapshot = await getDocs(monthlyGraphsQuery);
      
      monthlyGraphsSnapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
        rollbackSteps.push(`Deleted monthly graph: ${doc.id}`);
      });

      // Step 2: Find and delete ONLY the specific month's ledger entry (transection_type: 3)
      setRollbackProgress(20);
      const ledgersQuery = query(
        collection(DATABASE, "ledgers"),
        where("user_id", "==", selectedClosure.user_id),
        where("transection_type", "==", 3)
      );
      const ledgersSnapshot = await getDocs(ledgersQuery);
      
      // Only delete ledger entries that match the specific month/year
      // CRITICAL: Monthly closings are ALWAYS processed at the start of the NEXT month
      // Example: October closing (month=9) creates a performance ledger dated Nov 1-15
      // So when rolling back October, we look for ledgers dated in November (first 15 days)
      ledgersSnapshot.docs.forEach((doc) => {
        const ledgerData = doc.data();
        const ledgerDate = ledgerData.date?.toDate ? ledgerData.date.toDate() : new Date(ledgerData.date);
        const ledgerMonth = ledgerDate.getMonth(); // JavaScript Date is 0-based (0-11)
        const ledgerYear = ledgerDate.getFullYear();
        const closingMonth = selectedClosure.closing_month; // closing_month is also 0-based (0-11)
        const closingYear = selectedClosure.closing_year;
        
        // Performance for closingMonth is reported in the NEXT month
        // Calculate the reporting month (month after closing month)
        const reportingMonth = closingMonth === 11 ? 0 : closingMonth + 1;
        const reportingYear = closingMonth === 11 ? closingYear + 1 : closingYear;
        
        // Match if ledger is in the reporting month (first 15 days)
        const isInReportingMonth = (
          ledgerMonth === reportingMonth && 
          ledgerYear === reportingYear && 
          ledgerDate.getDate() <= 15
        );
        
        if (isInReportingMonth) {
          batch.delete(doc.ref);
          rollbackSteps.push(`Deleted ledger entry: ${doc.id} (dated ${ledgerDate.toISOString().split('T')[0]})`);
        }
      });

      // Step 3: Find and delete ONLY the specific month's transaction request (request_type: 2)
      setRollbackProgress(30);
      const transactionsQuery = query(
        collection(DATABASE, "transection_requests"),
        where("user_id", "==", selectedClosure.user_id),
        where("request_type", "==", 2)
      );
      const transactionsSnapshot = await getDocs(transactionsQuery);
      
      // Only delete transaction entries that match the specific month/year
      // CRITICAL: Performance transactions are ALWAYS created at the start of the NEXT month
      // Example: October closing (month=9) creates a transaction dated Nov 1-15
      // So when rolling back October, we look for transactions dated in November (first 15 days)
      transactionsSnapshot.docs.forEach((doc) => {
        const transactionData = doc.data();
        const transactionDate = transactionData.date?.toDate ? transactionData.date.toDate() : new Date(transactionData.date);
        const transactionMonth = transactionDate.getMonth(); // JavaScript Date is 0-based (0-11)
        const transactionYear = transactionDate.getFullYear();
        const closingMonth = selectedClosure.closing_month; // closing_month is also 0-based (0-11)
        const closingYear = selectedClosure.closing_year;
        
        // Performance for closingMonth is reported in the NEXT month
        // Calculate the reporting month (month after closing month)
        const reportingMonth = closingMonth === 11 ? 0 : closingMonth + 1;
        const reportingYear = closingMonth === 11 ? closingYear + 1 : closingYear;
        
        // Match if transaction is in the reporting month (first 15 days)
        const isInReportingMonth = (
          transactionMonth === reportingMonth && 
          transactionYear === reportingYear && 
          transactionDate.getDate() <= 15
        );
        
        if (isInReportingMonth) {
          batch.delete(doc.ref);
          rollbackSteps.push(`Deleted transaction: ${doc.id} (dated ${transactionDate.toISOString().split('T')[0]})`);
        }
      });

      // Step 4: Delete monthly closing entry
      setRollbackProgress(40);
      batch.delete(doc(DATABASE, "monthly_closings", selectedClosure.id));
      rollbackSteps.push(`Deleted monthly closing: ${selectedClosure.id}`);

      // Step 5: Yearly Graph - SKIP DELETION (GLOBAL ENTRY SHARED BY ALL USERS)
      // NOTE: yearly_graph entries are GLOBAL (one per month for entire fund)
      // They are NOT per-user, so deleting them would affect all users
      // Rolling back one user's closure should not impact the fund-level graph
      // If you need to rollback ALL users for a month, manually delete the yearly_graph entry
      setRollbackProgress(50);
      rollbackSteps.push(`⚠️ SKIPPED yearly_graph deletion (global entry, shared by all users)`);

      // Step 6: Restore user's net_balance by subtracting profit_loss from current balance
      setRollbackProgress(60);
      const userDocRef = doc(DATABASE, "users", selectedClosure.user_id);
      const userSnapshot = await getDoc(userDocRef);
      
      if (!userSnapshot.exists()) {
        throw new Error(`User document not found: ${selectedClosure.user_id}`);
      }
      
      const currentNetBalance = userSnapshot.data()?.net_balance || 0;
      const restoredBalance = currentNetBalance - selectedClosure.profit_loss;
      
      batch.update(userDocRef, {
        net_balance: restoredBalance,
        last_rollback: {
          date: new Date(),
          closure_id: selectedClosure.id,
          previous_balance: currentNetBalance,
          restored_balance: restoredBalance,
          removed_profit: selectedClosure.profit_loss
        }
      });
      rollbackSteps.push(`Restored user balance from $${currentNetBalance.toFixed(2)} to $${restoredBalance.toFixed(2)} (removed profit: $${selectedClosure.profit_loss.toFixed(2)})`);

      // Execute all operations
      setRollbackProgress(80);
      await batch.commit();

      setRollbackProgress(100);

      Swal.fire({
        icon: "success",
        title: "Rollback Successful!",
        html: `
          <div>
            <p><strong>Monthly closure successfully rolled back for:</strong></p>
            <p>User: ${selectedClosure.userEmail}</p>
            <p>Month: ${dayjs().month(selectedClosure.closing_month).year(selectedClosure.closing_year).format("MMMM YYYY")}</p>
            <p>Balance restored to: $${restoredBalance.toFixed(2)}</p>
            <p>Profit removed: $${selectedClosure.profit_loss.toFixed(2)}</p>
            <br>
            <p><strong>Operations performed:</strong></p>
            <ul style="text-align: left; font-size: 12px;">
              ${rollbackSteps.map(step => `<li>${step}</li>`).join('')}
            </ul>
          </div>
        `,
        width: 600,
      });

      setRollbackDialogOpen(false);
      setSelectedClosure(null);
      loadMonthlyClosures();
    } catch (error) {
      console.error("Error during rollback:", error);
      Swal.fire({
        icon: "error",
        title: "Rollback Failed",
        text: `Failed to rollback monthly closure: ${error.message}`,
      });
    } finally {
      setRollbackProgress(0);
    }
  };

  // Extract the rollback logic into a reusable function
  const performSingleRollback = async (closure, batch = null) => {
    const rollbackSteps = [];
    const ownBatch = batch === null;
    const batchToUse = batch || writeBatch(DATABASE);

    try {
      // Step 1: Find and delete monthly_grpahs entry
      const monthlyGraphsQuery = query(
        collection(DATABASE, "monthly_grpahs"),
        where("user_id", "==", closure.user_id),
        where("graph_month", "==", closure.closing_month),
        where("graph_year", "==", closure.closing_year)
      );
      const monthlyGraphsSnapshot = await getDocs(monthlyGraphsQuery);
      
      monthlyGraphsSnapshot.docs.forEach((doc) => {
        batchToUse.delete(doc.ref);
        rollbackSteps.push(`Deleted monthly graph: ${doc.id}`);
      });

      // Step 2: Find and delete ONLY the specific month's ledger entry (transection_type: 3)
      const ledgersQuery = query(
        collection(DATABASE, "ledgers"),
        where("user_id", "==", closure.user_id),
        where("transection_type", "==", 3)
      );
      const ledgersSnapshot = await getDocs(ledgersQuery);
      
      ledgersSnapshot.docs.forEach((doc) => {
        const ledgerData = doc.data();
        const ledgerDate = ledgerData.date?.toDate ? ledgerData.date.toDate() : new Date(ledgerData.date);
        const ledgerMonth = ledgerDate.getMonth();
        const ledgerYear = ledgerDate.getFullYear();
        const closingMonth = closure.closing_month;
        const closingYear = closure.closing_year;
        
        // Performance for closingMonth is reported in the NEXT month
        // Calculate the reporting month (month after closing month)
        const reportingMonth = closingMonth === 11 ? 0 : closingMonth + 1;
        const reportingYear = closingMonth === 11 ? closingYear + 1 : closingYear;
        
        // Match if ledger is in the reporting month (first 15 days)
        const isInReportingMonth = (
          ledgerMonth === reportingMonth && 
          ledgerYear === reportingYear && 
          ledgerDate.getDate() <= 15
        );
        
        if (isInReportingMonth) {
          batchToUse.delete(doc.ref);
          rollbackSteps.push(`Deleted ledger entry: ${doc.id} (dated ${ledgerDate.toISOString().split('T')[0]})`);
        }
      });

      // Step 3: Find and delete ONLY the specific month's transaction request (request_type: 2)
      const transactionsQuery = query(
        collection(DATABASE, "transection_requests"),
        where("user_id", "==", closure.user_id),
        where("request_type", "==", 2)
      );
      const transactionsSnapshot = await getDocs(transactionsQuery);
      
      transactionsSnapshot.docs.forEach((doc) => {
        const transactionData = doc.data();
        const transactionDate = transactionData.date?.toDate ? transactionData.date.toDate() : new Date(transactionData.date);
        const transactionMonth = transactionDate.getMonth();
        const transactionYear = transactionDate.getFullYear();
        const closingMonth = closure.closing_month;
        const closingYear = closure.closing_year;
        
        // Performance for closingMonth is reported in the NEXT month
        // Calculate the reporting month (month after closing month)
        const reportingMonth = closingMonth === 11 ? 0 : closingMonth + 1;
        const reportingYear = closingMonth === 11 ? closingYear + 1 : closingYear;
        
        // Match if transaction is in the reporting month (first 15 days)
        const isInReportingMonth = (
          transactionMonth === reportingMonth && 
          transactionYear === reportingYear && 
          transactionDate.getDate() <= 15
        );
        
        if (isInReportingMonth) {
          batchToUse.delete(doc.ref);
          rollbackSteps.push(`Deleted transaction: ${doc.id} (dated ${transactionDate.toISOString().split('T')[0]})`);
        }
      });

      // Step 4: Delete monthly closing entry
      batchToUse.delete(doc(DATABASE, "monthly_closings", closure.id));
      rollbackSteps.push(`Deleted monthly closing: ${closure.id}`);

      // Step 5: Restore user's net_balance by subtracting profit_loss from current balance
      const userDocRef = doc(DATABASE, "users", closure.user_id);
      const userSnapshot = await getDoc(userDocRef);
      
      if (!userSnapshot.exists()) {
        throw new Error(`User document not found: ${closure.user_id}`);
      }
      
      const currentNetBalance = userSnapshot.data()?.net_balance || 0;
      const restoredBalance = currentNetBalance - closure.profit_loss;
      
      batchToUse.update(userDocRef, {
        net_balance: restoredBalance,
        last_rollback: {
          date: new Date(),
          closure_id: closure.id,
          previous_balance: currentNetBalance,
          restored_balance: restoredBalance,
          removed_profit: closure.profit_loss
        }
      });
      rollbackSteps.push(`Restored user balance from $${currentNetBalance.toFixed(2)} to $${restoredBalance.toFixed(2)} (removed profit: $${closure.profit_loss.toFixed(2)})`);

      // Commit if this is its own batch
      if (ownBatch) {
        await batchToUse.commit();
      }

      return { success: true, steps: rollbackSteps };
    } catch (error) {
      console.error("Error performing rollback:", error);
      return { success: false, error: error.message, steps: rollbackSteps };
    }
  };

  // Batch rollback: Get preview of users for selected month
  const handlePreviewBatchRollback = async () => {
    if (!selectedBatchMonth || !selectedBatchYear) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please select both month and year",
      });
      return;
    }

    setLoading(true);
    try {
      const monthIndex = parseInt(selectedBatchMonth);
      const yearNumber = parseInt(selectedBatchYear);
      
      console.log('🔍 DEBUG: Rollback Preview Query:');
      console.log(`   Month Index: ${monthIndex} (0-based, so month ${monthIndex + 1})`);
      console.log(`   Year: ${yearNumber}`);
      
      const closuresQuery = query(
        collection(DATABASE, "monthly_closings"),
        where("closing_month", "==", monthIndex),
        where("closing_year", "==", yearNumber)
      );
      const closuresSnapshot = await getDocs(closuresQuery);
      
      console.log(`   Found ${closuresSnapshot.docs.length} records in monthly_closings with filters`);
      
      // DEBUG: Also check ALL records for this month to see if any are missing
      const allClosingsQuery = query(
        collection(DATABASE, "monthly_closings"),
        orderBy("date", "desc")
      );
      const allClosingsSnapshot = await getDocs(allClosingsQuery);
      
      const allMonthClosings = allClosingsSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(data => {
          const month = data.closing_month;
          const year = data.closing_year;
          // Check if it matches the selected month/year
          return month === monthIndex && year === yearNumber;
        });
      
      console.log(`   DEBUG: All ${monthIndex + 1}/${yearNumber} closings (from full query): ${allMonthClosings.length}`);
      console.log(`   DEBUG: Records found by filtered query: ${closuresSnapshot.docs.length}`);
      console.log(`   DEBUG: Difference: ${allMonthClosings.length - closuresSnapshot.docs.length} records may be missing`);
      
      if (allMonthClosings.length !== closuresSnapshot.docs.length) {
        console.warn('⚠️ WARNING: Some records may have incorrect closing_month or closing_year values!');
        console.warn('   Records found in full query but not in filtered query:');
        allMonthClosings.forEach(closure => {
          const foundInFiltered = closuresSnapshot.docs.some(doc => doc.id === closure.id);
          if (!foundInFiltered) {
            console.warn(`   ❌ Missing: ${closure.id}, user_id: ${closure.user_id}, closing_month: ${closure.closing_month}, closing_year: ${closure.closing_year}, profit_loss: ${closure.profit_loss}`);
          }
        });
      }
      
      const usersData = await Promise.all(
        closuresSnapshot.docs.map(async (closureDoc) => {
          const closureData = closureDoc.data();
          
          // DEBUG: Log each record being processed
          console.log(`   ✅ Processing closure: ${closureDoc.id}, user_id: ${closureData.user_id}, profit_loss: $${closureData.profit_loss?.toFixed(2) || '0.00'}`);
          
          // Get user information
          let userEmail = "Unknown";
          let userName = "Unknown";
          let userType = null;
          if (closureData.user_id) {
            try {
              const userDocRef = doc(DATABASE, "users", closureData.user_id);
              const userDocSnap = await getDoc(userDocRef);
              if (userDocSnap.exists()) {
                const userData = userDocSnap.data();
                userEmail = userData.email || "Unknown";
                userName = userData.full_name || "Unknown";
                userType = userData.user_type !== undefined ? userData.user_type : null;
              }
            } catch (error) {
              console.error("Error fetching user data:", error);
            }
          }

          return {
            id: closureDoc.id,
            ...closureData,
            userEmail,
            userName,
            userType,
          };
        })
      );

      // Sort users alphabetically by name
      const sortedUsers = usersData.sort((a, b) => 
        (a.userName || '').localeCompare(b.userName || '', undefined, { sensitivity: 'base' })
      );
      
      // DEBUG: Log totals calculation
      const individualUsers = sortedUsers.filter(user => user.userType === 0 || user.userType === null || user.userType === undefined);
      const corporateUsers = sortedUsers.filter(user => user.userType === 1);
      const individualTotal = individualUsers.reduce((sum, user) => sum + (parseFloat(user.profit_loss) || 0), 0);
      const corporateTotal = corporateUsers.reduce((sum, user) => sum + (parseFloat(user.profit_loss) || 0), 0);
      
      console.log('📊 DEBUG: Rollback Preview Totals:');
      console.log(`   Individual: ${individualUsers.length} clients, Total: $${individualTotal.toFixed(2)}`);
      console.log(`   Corporate: ${corporateUsers.length} clients, Total: $${corporateTotal.toFixed(2)}`);
      console.log(`   Grand Total: $${(individualTotal + corporateTotal).toFixed(2)}`);
      console.log(`   Total users: ${sortedUsers.length}`);

      setBatchPreviewUsers(sortedUsers);
      setBatchDialogOpen(true);
    } catch (error) {
      console.error("Error loading batch preview:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: `Failed to load users for batch rollback: ${error.message}`,
      });
    } finally {
      setLoading(false);
    }
  };

  // Execute batch rollback
  const confirmBatchRollback = async () => {
    // Allow execution if either:
    // 1. There are users to roll back, OR
    // 2. There are no users BUT deleteYearlyGraph is checked (for cleaning up orphaned yearly_graph entries)
    if (batchPreviewUsers.length === 0 && !deleteYearlyGraph) {
      Swal.fire({
        icon: "warning",
        title: "No Users to Rollback",
        text: "There are no users with closures for this month. If you want to delete the yearly graph entry only, check the checkbox.",
      });
      return;
    }

    setBatchProgress({ current: 0, total: batchPreviewUsers.length });
    setBatchResults({ success: [], failed: [] });

    const results = { success: [], failed: [] };

    try {
      // Process each user (if any)
      for (let i = 0; i < batchPreviewUsers.length; i++) {
        const closure = batchPreviewUsers[i];
        setBatchProgress({ current: i + 1, total: batchPreviewUsers.length });

        try {
          const result = await performSingleRollback(closure, null);
          if (result.success) {
            results.success.push({ email: closure.userEmail, steps: result.steps });
          } else {
            results.failed.push({ email: closure.userEmail, error: result.error });
          }
        } catch (error) {
          results.failed.push({ email: closure.userEmail, error: error.message });
        }
      }

      // Delete yearly_graph entry if requested
      if (deleteYearlyGraph) {
        try {
          const yearlyGraphQuery = query(
            collection(DATABASE, "yearly_graph"),
            where("month", "==", parseInt(selectedBatchMonth) + 1), // yearly_graph uses 1-based months
            where("year", "==", parseInt(selectedBatchYear))
          );
          const yearlyGraphSnapshot = await getDocs(yearlyGraphQuery);
          
          const batch = writeBatch(DATABASE);
          yearlyGraphSnapshot.docs.forEach((doc) => {
            batch.delete(doc.ref);
          });
          await batch.commit();
          
          results.yearlyGraphDeleted = yearlyGraphSnapshot.docs.length;
          
          // Also update consolidated document
          try {
            const { removeMonthFromSummary } = await import("../../../../services/yearlyGraphService");
            await removeMonthFromSummary(
              parseInt(selectedBatchYear),
              parseInt(selectedBatchMonth) + 1
            );
            console.log(`✅ Removed month from consolidated document and recalculated subsequent points`);
          } catch (consolidatedError) {
            console.error("❌ Error updating consolidated document during rollback:", consolidatedError);
            results.yearlyGraphConsolidatedError = consolidatedError.message;
          }
        } catch (error) {
          console.error("Error deleting yearly graph:", error);
          results.yearlyGraphError = error.message;
        }
      }

      setBatchResults(results);

      // Show success dialog
      const monthName = dayjs().month(parseInt(selectedBatchMonth)).format("MMMM");
      
      // Determine dialog type
      let dialogIcon = "success";
      let dialogTitle = "Batch Rollback Complete!";
      
      if (batchPreviewUsers.length === 0 && deleteYearlyGraph) {
        // Special case: only yearly graph deletion
        dialogTitle = "Yearly Graph Deleted!";
      } else if (results.failed.length > 0) {
        dialogIcon = "warning";
      }
      
      Swal.fire({
        icon: dialogIcon,
        title: dialogTitle,
        html: `
          <div style="text-align: left;">
            <p><strong>Month:</strong> ${monthName} ${selectedBatchYear}</p>
            ${batchPreviewUsers.length > 0 ? `
              <p><strong>✅ Successfully rolled back:</strong> ${results.success.length} users</p>
              ${results.failed.length > 0 ? `<p><strong>❌ Failed:</strong> ${results.failed.length} users</p>` : ''}
            ` : `
              <p><strong>ℹ️ No users to rollback</strong> (0 closures for this month)</p>
            `}
            ${deleteYearlyGraph && results.yearlyGraphDeleted ? `<p><strong>🗑️ Yearly graph entries deleted:</strong> ${results.yearlyGraphDeleted}</p>` : ''}
            ${deleteYearlyGraph && !results.yearlyGraphDeleted && !results.yearlyGraphError ? `<p><strong>ℹ️ No yearly graph entries found for this month</strong></p>` : ''}
            ${results.yearlyGraphError ? `<p style="color: red;"><strong>⚠️ Yearly graph error:</strong> ${results.yearlyGraphError}</p>` : ''}
            
            ${results.failed.length > 0 ? `
              <br><strong>Failed users:</strong>
              <ul style="font-size: 12px;">
                ${results.failed.map(f => `<li>${f.email}: ${f.error}</li>`).join('')}
              </ul>
            ` : ''}
          </div>
        `,
        width: 600,
      });

      setBatchDialogOpen(false);
      setBatchPreviewUsers([]);
      setSelectedBatchMonth("");
      setSelectedBatchYear("");
      setDeleteYearlyGraph(false);
      loadMonthlyClosures();
    } catch (error) {
      console.error("Error during batch rollback:", error);
      Swal.fire({
        icon: "error",
        title: "Batch Rollback Failed",
        text: `An error occurred during batch rollback: ${error.message}`,
      });
    } finally {
      setBatchProgress({ current: 0, total: 0 });
    }
  };

  // Full Rollback: Preview orphaned data
  const handlePreviewFullRollback = async () => {
    if (!selectedFullRollbackMonth || !selectedFullRollbackYear) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please select both month and year",
      });
      return;
    }

    setLoading(true);
    setFullRollbackProgress(0);
    try {
      const monthIndex = parseInt(selectedFullRollbackMonth);
      const yearNumber = parseInt(selectedFullRollbackYear);
      
      // Reporting month for ledgers (month after closing month)
      const reportingMonth = monthIndex === 11 ? 0 : monthIndex + 1;
      const reportingYear = monthIndex === 11 ? yearNumber + 1 : yearNumber;
      
      // Date range for ledgers (first 15 days of reporting month)
      const reportingStartDate = new Date(reportingYear, reportingMonth, 1);
      const reportingEndDate = new Date(reportingYear, reportingMonth, 16);
      
      setFullRollbackProgress(10);
      
      // Step 1: Find monthly_grpahs entries (orphaned data)
      console.log(`🔍 Searching monthly_grpahs for month ${monthIndex}, year ${yearNumber}...`);
      const monthlyGraphsQuery = query(
        collection(DATABASE, "monthly_grpahs"),
        where("graph_month", "==", monthIndex),
        where("graph_year", "==", yearNumber)
      );
      const monthlyGraphsSnapshot = await getDocs(monthlyGraphsQuery);
      const monthlyGraphsCount = monthlyGraphsSnapshot.size;
      
      setFullRollbackProgress(30);
      
      // Get all user_ids and matching info from monthly_grpahs entries
      const userIdsFromMonthlyGraphs = new Set();
      const monthlyGraphsMatchingInfo = new Map(); // user_id -> { timestamp, profitLossPercent, docId }
      
      monthlyGraphsSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        const docId = doc.id;
        
        if (data.user_id) {
          const userId = data.user_id?.doc_id || data.user_id?.id || data.user_id;
          if (userId) {
            userIdsFromMonthlyGraphs.add(userId);
            
            // Extract timestamp from doc_id: format is {user_id}_{YYYYMMDDHHmmss}
            const parts = docId.split('_');
            if (parts.length >= 2) {
              const timestamp = parts.slice(1).join('_'); // Get everything after first underscore
              monthlyGraphsMatchingInfo.set(userId, {
                timestamp: timestamp,
                profitLossPercent: data.gross_performance_percentage || data.profit_loss_percent || data.total_Monthly_percantage || 0,
                docId: docId
              });
            }
          }
        }
      });
      
      // Step 2: Find ledger entries (type 3) - match precisely by timestamp or date + user
      console.log(`🔍 Searching ledgers for reporting period (${reportingStartDate.toISOString().split('T')[0]} to ${reportingEndDate.toISOString().split('T')[0]}) or today...`);
      const ledgersQuery = query(
        collection(DATABASE, "ledgers"),
        where("transection_type", "==", 3)
      );
      const ledgersSnapshot = await getDocs(ledgersQuery);
      
      const septemberLedgers = [];
      const usersToRestore = new Map();
      
      // Today's date range (for transactions created today)
      const todayStart = dayjs().startOf("day").toDate();
      const todayEnd = dayjs().endOf("day").toDate();
      
      // FALLBACK MODE: If no monthly_grpahs entries exist, look for orphaned transactions from today
      const hasMonthlyGraphsEntries = monthlyGraphsCount > 0;
      
      ledgersSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        const ledgerDate = data.date?.toDate ? data.date.toDate() : new Date(data.date);
        const userId = data.user_id;
        const docId = doc.id;
        
        let shouldMatch = false;
        
        if (hasMonthlyGraphsEntries) {
          // Normal mode: Match using monthly_grpahs entries
          if (!userIdsFromMonthlyGraphs.has(userId)) {
            return; // User not in monthly_grpahs, skip
          }
          
          // Get matching info for this user
          const matchingInfo = monthlyGraphsMatchingInfo.get(userId);
          
          // Extract timestamp from ledger doc_id
          const parts = docId.split('_');
          const ledgerTimestamp = parts.length >= 2 ? parts.slice(1).join('_') : null;
          
          // Match criteria:
          // 1. Timestamp in doc_id matches exactly, OR
          // 2. Date is in reporting period, OR
          // 3. Date is today AND profit_loss_percent matches (within 0.01 tolerance)
          const timestampMatches = ledgerTimestamp && matchingInfo && ledgerTimestamp === matchingInfo.timestamp;
          const isInReportingPeriod = ledgerDate >= reportingStartDate && ledgerDate < reportingEndDate;
          // Include yesterday and today for recent closures (last 24-48 hours)
          const yesterdayStart = dayjs().subtract(1, "day").startOf("day").toDate();
          const yesterdayEnd = dayjs().subtract(1, "day").endOf("day").toDate();
          const isRecentDate = 
            (ledgerDate >= yesterdayStart && ledgerDate <= yesterdayEnd) ||
            (ledgerDate >= todayStart && ledgerDate <= todayEnd);
          
          const isRecentAndPercentMatches = 
            matchingInfo &&
            isRecentDate &&
            Math.abs((data.profit_loss_percent || 0) - matchingInfo.profitLossPercent) < 0.01;
          
          shouldMatch = timestampMatches || isInReportingPeriod || isRecentAndPercentMatches;
        } else {
          // FALLBACK MODE: No monthly_grpahs entries, look for orphaned transactions
          // Match if date is today, yesterday (last 24-48 hours), OR in reporting period
          const yesterdayStart = dayjs().subtract(1, "day").startOf("day").toDate();
          const yesterdayEnd = dayjs().subtract(1, "day").endOf("day").toDate();
          
          const isYesterday = ledgerDate >= yesterdayStart && ledgerDate <= yesterdayEnd;
          const isToday = ledgerDate >= todayStart && ledgerDate <= todayEnd;
          const isInReportingPeriod = ledgerDate >= reportingStartDate && ledgerDate < reportingEndDate;
          
          shouldMatch = isYesterday || isToday || isInReportingPeriod;
        }
        
        if (shouldMatch) {
          septemberLedgers.push({ id: doc.id, ...data });
          
          // Track user balance restoration
          if (userId && data.profit_loss_balance) {
            const profitLoss = parseFloat(data.profit_loss_balance) || 0;
            if (!usersToRestore.has(userId)) {
              usersToRestore.set(userId, {
                userId: userId,
                totalProfitLoss: 0,
                currentBalance: data.current_balance,
                ledgerIds: []
              });
            }
            const userData = usersToRestore.get(userId);
            userData.totalProfitLoss += profitLoss;
            userData.ledgerIds.push(doc.id);
          }
        }
      });
      
      setFullRollbackProgress(50);
      
      // Step 3: Find transection_requests (type 2) in reporting period OR created today
      console.log(`🔍 Searching transection_requests for reporting period or today...`);
      const transectionRequestsQuery = query(
        collection(DATABASE, "transection_requests"),
        where("request_type", "==", 2)
      );
      const transectionRequestsSnapshot = await getDocs(transectionRequestsQuery);
      
      const septemberTransectionRequests = [];
      transectionRequestsSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        const transDate = data.date?.toDate ? data.date.toDate() : new Date(data.date);
        const userId = data.user_id;
        const docId = doc.id;
        
        let shouldMatch = false;
        
        if (hasMonthlyGraphsEntries) {
          // Normal mode: Match using monthly_grpahs entries
          if (!userIdsFromMonthlyGraphs.has(userId)) {
            return; // User not in monthly_grpahs, skip
          }
          
          // Get matching info for this user
          const matchingInfo = monthlyGraphsMatchingInfo.get(userId);
          
          if (!matchingInfo) {
            return; // No matching info, skip
          }
          
          // Extract timestamp from transaction doc_id
          const parts = docId.split('_');
          const transTimestamp = parts.length >= 2 ? parts.slice(1).join('_') : null;
          
          // Match criteria:
          // 1. Timestamp in doc_id matches exactly, OR
          // 2. Date is in reporting period, OR
          // 3. Date is today AND profit_loss_percent matches (within 0.01 tolerance)
          const timestampMatches = transTimestamp && transTimestamp === matchingInfo.timestamp;
          const isInReportingPeriod = transDate >= reportingStartDate && transDate < reportingEndDate;
          // Include yesterday and today for recent closures (last 24-48 hours)
          const yesterdayStart = dayjs().subtract(1, "day").startOf("day").toDate();
          const yesterdayEnd = dayjs().subtract(1, "day").endOf("day").toDate();
          const isRecentDate = 
            (transDate >= yesterdayStart && transDate <= yesterdayEnd) ||
            (transDate >= todayStart && transDate <= todayEnd);
          
          const isRecentAndPercentMatches = 
            isRecentDate &&
            Math.abs((data.profit_loss_percent || 0) - matchingInfo.profitLossPercent) < 0.01;
          
          shouldMatch = timestampMatches || isInReportingPeriod || isRecentAndPercentMatches;
        } else {
          // FALLBACK MODE: No monthly_grpahs entries, look for orphaned transactions
          // Match if date is today, yesterday (last 24-48 hours), OR in reporting period
          const yesterdayStart = dayjs().subtract(1, "day").startOf("day").toDate();
          const yesterdayEnd = dayjs().subtract(1, "day").endOf("day").toDate();
          
          const isYesterday = transDate >= yesterdayStart && transDate <= yesterdayEnd;
          const isToday = transDate >= todayStart && transDate <= todayEnd;
          const isInReportingPeriod = transDate >= reportingStartDate && transDate < reportingEndDate;
          
          shouldMatch = isYesterday || isToday || isInReportingPeriod;
        }
        
        if (shouldMatch) {
          septemberTransectionRequests.push({ id: doc.id, ...data });
          console.log(`✅ Preview: Matched transaction ${docId} for user ${userId} (fallback mode: ${!hasMonthlyGraphsEntries})`);
        }
      });
      
      setFullRollbackProgress(70);
      
      // Step 4: Find monthly_closings entries (if any)
      const monthlyClosingsQuery = query(
        collection(DATABASE, "monthly_closings"),
        where("closing_month", "==", monthIndex),
        where("closing_year", "==", yearNumber)
      );
      const monthlyClosingsSnapshot = await getDocs(monthlyClosingsQuery);
      
      setFullRollbackProgress(85);
      
      // Step 5: Collect detailed user information
      const userDetailsList = [];
      const allUserIds = new Set([...userIdsFromMonthlyGraphs]);
      
      // Also add user IDs from monthly_closings
      monthlyClosingsSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        const userId = data.user_id?.doc_id || data.user_id?.id || data.user_id;
        if (userId) {
          allUserIds.add(userId);
        }
      });
      
      // Fetch user details and monthly closing data
      for (const userId of allUserIds) {
        try {
          // Get user info
          let userDocRef = doc(DATABASE, "users", userId);
          let userDoc = await getDoc(userDocRef);
          
          if (!userDoc.exists()) {
            // Try query by doc_id
            const userQuery = query(
              collection(DATABASE, "users"),
              where("doc_id", "==", userId)
            );
            const userSnapshot = await getDocs(userQuery);
            if (!userSnapshot.empty) {
              userDoc = userSnapshot.docs[0];
              userDocRef = doc(DATABASE, "users", userDoc.id);
            }
          }
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            
            // Get monthly_closing data for this user
            const userClosingQuery = query(
              collection(DATABASE, "monthly_closings"),
              where("user_id", "==", userId),
              where("closing_month", "==", monthIndex),
              where("closing_year", "==", yearNumber)
            );
            const userClosingSnapshot = await getDocs(userClosingQuery);
            
            // Get monthly_grpahs data for this user
            const userGraphQuery = query(
              collection(DATABASE, "monthly_grpahs"),
              where("user_id", "==", userId),
              where("graph_month", "==", monthIndex),
              where("graph_year", "==", yearNumber)
            );
            const userGraphSnapshot = await getDocs(userGraphQuery);
            
            let closingData = null;
            let graphData = null;
            
            if (!userClosingSnapshot.empty) {
              closingData = userClosingSnapshot.docs[0].data();
            }
            
            if (!userGraphSnapshot.empty) {
              graphData = userGraphSnapshot.docs[0].data();
            }
            
            // Use closing data if available, otherwise use graph data
            const performanceData = closingData || graphData;
            
            if (performanceData) {
              const profitLoss = parseFloat(performanceData.profit_to_user || performanceData.profit_loss || 0);
              const performancePercent = parseFloat(
                performanceData.gross_performance_percentage || 
                performanceData.profit_loss_percent || 
                performanceData.total_Monthly_percantage || 
                performanceData.performance_percentage || 
                0
              );
              const closingBalance = parseFloat(performanceData.closing_balance || performanceData.balance_after_commission || 0);
              
              userDetailsList.push({
                userId: userId,
                userName: userData.full_name || "Unknown",
                userEmail: userData.email || "Unknown",
                userType: userData.user_type !== undefined ? userData.user_type : null,
                profitLoss: profitLoss,
                performancePercent: performancePercent,
                closingBalance: closingBalance,
                hasMonthlyClosing: !userClosingSnapshot.empty,
                hasMonthlyGraph: !userGraphSnapshot.empty
              });
            }
          }
        } catch (error) {
          console.error(`Error fetching user details for ${userId}:`, error);
        }
      }
      
      // Sort by user type (Individual first, then Corporate), then by name
      userDetailsList.sort((a, b) => {
        if (a.userType !== b.userType) {
          if (a.userType === 0) return -1; // Individual first
          if (b.userType === 0) return 1;
          return 0;
        }
        return a.userName.localeCompare(b.userName);
      });
      
      setFullRollbackProgress(100);
      
      // Update preview state
      setFullRollbackPreview({
        monthly_grpahs: monthlyGraphsCount,
        ledgers: septemberLedgers.length,
        transection_requests: septemberTransectionRequests.length,
        monthly_closings: monthlyClosingsSnapshot.size,
        usersToRestore: usersToRestore.size
      });
      
      setFullRollbackUserDetails(userDetailsList);
      setFullRollbackDialogOpen(true);
    } catch (error) {
      console.error("Error loading full rollback preview:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: `Failed to load orphaned data: ${error.message}`,
      });
    } finally {
      setLoading(false);
      setFullRollbackProgress(0);
    }
  };

  // Full Rollback: Execute cleanup
  const confirmFullRollback = async () => {
    const totalEntries = fullRollbackPreview.monthly_grpahs + 
                         fullRollbackPreview.ledgers + 
                         fullRollbackPreview.transection_requests + 
                         fullRollbackPreview.monthly_closings;
    
    if (totalEntries === 0 && !fullRollbackDeleteYearlyGraph) {
      Swal.fire({
        icon: "warning",
        title: "No Orphaned Data Found",
        text: "There is no orphaned data for this month. If you want to delete the yearly graph entry only, check the checkbox.",
      });
      return;
    }

    setFullRollbackProgress(0);
    
    try {
      const monthIndex = parseInt(selectedFullRollbackMonth);
      const yearNumber = parseInt(selectedFullRollbackYear);
      
      // Reporting month for ledgers
      const reportingMonth = monthIndex === 11 ? 0 : monthIndex + 1;
      const reportingYear = monthIndex === 11 ? yearNumber + 1 : yearNumber;
      
      // Date range for ledgers
      const reportingStartDate = new Date(reportingYear, reportingMonth, 1);
      const reportingEndDate = new Date(reportingYear, reportingMonth, 16);
      
      const batch = writeBatch(DATABASE);
      const rollbackSteps = [];
      let batchCount = 0;
      const BATCH_LIMIT = 500;
      
      // Step 1: Get monthly_grpahs entries and extract matching info BEFORE deleting
      setFullRollbackProgress(10);
      const monthlyGraphsQuery = query(
        collection(DATABASE, "monthly_grpahs"),
        where("graph_month", "==", monthIndex),
        where("graph_year", "==", yearNumber)
      );
      const monthlyGraphsSnapshot = await getDocs(monthlyGraphsQuery);
      
      // Extract matching info from monthly_grpahs entries BEFORE deleting them
      const userIdsFromMonthlyGraphs = new Set();
      const monthlyGraphsMatchingInfo = new Map(); // user_id -> { timestamp, profitLossPercent, docId }
      
      monthlyGraphsSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        const docId = doc.id;
        
        if (data.user_id) {
          const userId = data.user_id?.doc_id || data.user_id?.id || data.user_id;
          if (userId) {
            userIdsFromMonthlyGraphs.add(userId);
            
            // Extract timestamp from doc_id: format is {user_id}_{YYYYMMDDHHmmss}
            const parts = docId.split('_');
            if (parts.length >= 2) {
              const timestamp = parts.slice(1).join('_'); // Get everything after first underscore
              monthlyGraphsMatchingInfo.set(userId, {
                timestamp: timestamp,
                profitLossPercent: data.gross_performance_percentage || data.profit_loss_percent || data.total_Monthly_percantage || 0,
                docId: docId
              });
            }
          }
        }
      });
      
      // Now delete monthly_grpahs entries (if any exist)
      if (monthlyGraphsSnapshot.size > 0) {
        monthlyGraphsSnapshot.docs.forEach((doc) => {
          batch.delete(doc.ref);
          batchCount++;
          if (batchCount >= BATCH_LIMIT) {
            // Would need to commit and create new batch, but for simplicity, just track
          }
        });
        rollbackSteps.push(`Deleted ${monthlyGraphsSnapshot.size} monthly_grpahs entries`);
      } else {
        rollbackSteps.push(`No monthly_grpahs entries found (already deleted or never created)`);
      }
      
      // Step 2: Delete ledger entries with PRECISE matching or FALLBACK mode
      setFullRollbackProgress(30);
      
      const ledgersQuery = query(
        collection(DATABASE, "ledgers"),
        where("transection_type", "==", 3)
      );
      const ledgersSnapshot = await getDocs(ledgersQuery);
      
      const usersToRestore = new Map();
      let ledgerCount = 0;
      
      // Today's date range (for transactions created today)
      const todayStart = dayjs().startOf("day").toDate();
      const todayEnd = dayjs().endOf("day").toDate();
      
      // FALLBACK MODE: If no monthly_grpahs entries exist, look for orphaned transactions
      const hasMonthlyGraphsEntries = monthlyGraphsSnapshot.size > 0;
      
      ledgersSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        const ledgerDate = data.date?.toDate ? data.date.toDate() : new Date(data.date);
        const userId = data.user_id;
        const docId = doc.id;
        
        let shouldMatch = false;
        
        if (hasMonthlyGraphsEntries) {
          // Normal mode: Match using monthly_grpahs entries
          if (!userIdsFromMonthlyGraphs.has(userId)) {
            return; // User not in monthly_grpahs, skip
          }
          
          // Get matching info for this user
          const matchingInfo = monthlyGraphsMatchingInfo.get(userId);
          
          // Extract timestamp from ledger doc_id
          const parts = docId.split('_');
          const ledgerTimestamp = parts.length >= 2 ? parts.slice(1).join('_') : null;
          
          // Match criteria:
          // 1. Timestamp in doc_id matches exactly, OR
          // 2. Date is in reporting period, OR
          // 3. Date is today AND profit_loss_percent matches (within 0.01 tolerance)
          const timestampMatches = ledgerTimestamp && matchingInfo && ledgerTimestamp === matchingInfo.timestamp;
          const isInReportingPeriod = ledgerDate >= reportingStartDate && ledgerDate < reportingEndDate;
          // Include yesterday and today for recent closures (last 24-48 hours)
          const yesterdayStart = dayjs().subtract(1, "day").startOf("day").toDate();
          const yesterdayEnd = dayjs().subtract(1, "day").endOf("day").toDate();
          const isRecentDate = 
            (ledgerDate >= yesterdayStart && ledgerDate <= yesterdayEnd) ||
            (ledgerDate >= todayStart && ledgerDate <= todayEnd);
          
          const isRecentAndPercentMatches = 
            matchingInfo &&
            isRecentDate &&
            Math.abs((data.profit_loss_percent || 0) - matchingInfo.profitLossPercent) < 0.01;
          
          shouldMatch = timestampMatches || isInReportingPeriod || isRecentAndPercentMatches;
        } else {
          // FALLBACK MODE: No monthly_grpahs entries, look for orphaned transactions
          // Match if date is today, yesterday (last 24-48 hours), OR in reporting period
          const yesterdayStart = dayjs().subtract(1, "day").startOf("day").toDate();
          const yesterdayEnd = dayjs().subtract(1, "day").endOf("day").toDate();
          
          const isYesterday = ledgerDate >= yesterdayStart && ledgerDate <= yesterdayEnd;
          const isToday = ledgerDate >= todayStart && ledgerDate <= todayEnd;
          const isInReportingPeriod = ledgerDate >= reportingStartDate && ledgerDate < reportingEndDate;
          
          shouldMatch = isYesterday || isToday || isInReportingPeriod;
        }
        
        if (shouldMatch) {
          batch.delete(doc.ref);
          batchCount++;
          ledgerCount++;
          
          // Track user balance restoration
          if (userId && data.profit_loss_balance) {
            const profitLoss = parseFloat(data.profit_loss_balance) || 0;
            if (!usersToRestore.has(userId)) {
              usersToRestore.set(userId, {
                userId: userId,
                totalProfitLoss: 0,
                currentBalance: data.current_balance
              });
            }
            const userData = usersToRestore.get(userId);
            userData.totalProfitLoss += profitLoss;
          }
          console.log(`✅ Deleted ledger ${docId} for user ${userId} (fallback mode: ${!hasMonthlyGraphsEntries})`);
        }
      });
      rollbackSteps.push(`Deleted ${ledgerCount} ledger entries (${hasMonthlyGraphsEntries ? 'precisely matched' : 'fallback mode: orphaned transactions'})`);
      
      // Step 3: Delete transection_requests with PRECISE matching
      setFullRollbackProgress(50);
      const transectionRequestsQuery = query(
        collection(DATABASE, "transection_requests"),
        where("request_type", "==", 2)
      );
      const transectionRequestsSnapshot = await getDocs(transectionRequestsQuery);
      
      let transectionCount = 0;
      transectionRequestsSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        const transDate = data.date?.toDate ? data.date.toDate() : new Date(data.date);
        const userId = data.user_id;
        const docId = doc.id;
        
        let shouldMatch = false;
        
        if (hasMonthlyGraphsEntries) {
          // Normal mode: Match using monthly_grpahs entries
          if (!userIdsFromMonthlyGraphs.has(userId)) {
            return; // User not in monthly_grpahs, skip
          }
          
          // Get matching info for this user
          const matchingInfo = monthlyGraphsMatchingInfo.get(userId);
          
          if (!matchingInfo) {
            return; // No matching info, skip
          }
          
          // Extract timestamp from transaction doc_id
          const parts = docId.split('_');
          const transTimestamp = parts.length >= 2 ? parts.slice(1).join('_') : null;
          
          // Match criteria:
          // 1. Timestamp in doc_id matches exactly, OR
          // 2. Date is in reporting period, OR
          // 3. Date is today AND profit_loss_percent matches (within 0.01 tolerance)
          const timestampMatches = transTimestamp && transTimestamp === matchingInfo.timestamp;
          const isInReportingPeriod = transDate >= reportingStartDate && transDate < reportingEndDate;
          // Include yesterday and today for recent closures (last 24-48 hours)
          const yesterdayStart = dayjs().subtract(1, "day").startOf("day").toDate();
          const yesterdayEnd = dayjs().subtract(1, "day").endOf("day").toDate();
          const isRecentDate = 
            (transDate >= yesterdayStart && transDate <= yesterdayEnd) ||
            (transDate >= todayStart && transDate <= todayEnd);
          
          const isRecentAndPercentMatches = 
            isRecentDate &&
            Math.abs((data.profit_loss_percent || 0) - matchingInfo.profitLossPercent) < 0.01;
          
          shouldMatch = timestampMatches || isInReportingPeriod || isRecentAndPercentMatches;
        } else {
          // FALLBACK MODE: No monthly_grpahs entries, look for orphaned transactions
          // Match if date is today, yesterday (last 24-48 hours), OR in reporting period
          const yesterdayStart = dayjs().subtract(1, "day").startOf("day").toDate();
          const yesterdayEnd = dayjs().subtract(1, "day").endOf("day").toDate();
          
          const isYesterday = transDate >= yesterdayStart && transDate <= yesterdayEnd;
          const isToday = transDate >= todayStart && transDate <= todayEnd;
          const isInReportingPeriod = transDate >= reportingStartDate && transDate < reportingEndDate;
          
          shouldMatch = isYesterday || isToday || isInReportingPeriod;
        }
        
        if (shouldMatch) {
          batch.delete(doc.ref);
          batchCount++;
          transectionCount++;
          console.log(`✅ Deleted transaction ${docId} for user ${userId} (fallback mode: ${!hasMonthlyGraphsEntries})`);
        }
      });
      rollbackSteps.push(`Deleted ${transectionCount} transection_requests entries (${hasMonthlyGraphsEntries ? 'precisely matched' : 'fallback mode: orphaned transactions'})`);
      
      // Step 4: Delete monthly_closings (if any)
      setFullRollbackProgress(60);
      const monthlyClosingsQuery = query(
        collection(DATABASE, "monthly_closings"),
        where("closing_month", "==", monthIndex),
        where("closing_year", "==", yearNumber)
      );
      const monthlyClosingsSnapshot = await getDocs(monthlyClosingsQuery);
      
      monthlyClosingsSnapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
        batchCount++;
      });
      if (monthlyClosingsSnapshot.size > 0) {
        rollbackSteps.push(`Deleted ${monthlyClosingsSnapshot.size} monthly_closings entries`);
      }
      
      // Step 5: Delete yearly_graph entry if requested
      setFullRollbackProgress(70);
      if (fullRollbackDeleteYearlyGraph) {
        try {
          const yearlyGraphQuery = query(
            collection(DATABASE, "yearly_graph"),
            where("month", "==", monthIndex + 1), // yearly_graph uses 1-based months
            where("year", "==", yearNumber)
          );
          const yearlyGraphSnapshot = await getDocs(yearlyGraphQuery);
          
          yearlyGraphSnapshot.docs.forEach((doc) => {
            batch.delete(doc.ref);
          });
          
          rollbackSteps.push(`Deleted ${yearlyGraphSnapshot.size} yearly_graph entries`);
          
          // Also update consolidated document
          try {
            const { removeMonthFromSummary } = await import("../../../../services/yearlyGraphService");
            await removeMonthFromSummary(yearNumber, monthIndex + 1);
            rollbackSteps.push(`Updated consolidated yearly graph document`);
          } catch (consolidatedError) {
            console.error("Error updating consolidated document:", consolidatedError);
          }
        } catch (error) {
          console.error("Error deleting yearly graph:", error);
          rollbackSteps.push(`⚠️ Error deleting yearly_graph: ${error.message}`);
        }
      }
      
      // Commit all deletions
      setFullRollbackProgress(80);
      await batch.commit();
      
      // Step 6: Restore user balances
      setFullRollbackProgress(85);
      let usersRestored = 0;
      for (const [userId, userData] of usersToRestore) {
        try {
          const restoredBalance = userData.currentBalance - userData.totalProfitLoss;
          
          // First, try to find the document by doc_id field (in case document ID is different)
          const usersRef = collection(DATABASE, "users");
          const q = query(usersRef, where("doc_id", "==", userId));
          const querySnapshot = await getDocs(q);
          
          let userDocRef;
          if (!querySnapshot.empty) {
            // Found by doc_id field - use the actual document ID
            userDocRef = doc(DATABASE, "users", querySnapshot.docs[0].id);
          } else {
            // Try direct document ID lookup
            userDocRef = doc(DATABASE, "users", userId);
            const docSnap = await getDoc(userDocRef);
            if (!docSnap.exists()) {
              console.warn(`⚠️ User document not found for user_id: ${userId}. Skipping balance restoration.`);
              rollbackSteps.push(`⚠️ Skipped balance restoration for user ${userId} (document not found)`);
              continue; // Skip this user
            }
          }
          
          await updateDoc(userDocRef, {
            net_balance: restoredBalance
          });
          usersRestored++;
          rollbackSteps.push(`Restored user ${userId} balance: $${userData.currentBalance.toFixed(2)} → $${restoredBalance.toFixed(2)}`);
        } catch (error) {
          rollbackSteps.push(`⚠️ Error restoring balance for user ${userId}: ${error.message}`);
        }
      }
      
      if (usersToRestore.size > 0) {
        rollbackSteps.push(`Restored ${usersRestored} user balances`);
      }
      
      setFullRollbackProgress(100);
      
      Swal.fire({
        icon: "success",
        title: "Full Rollback Complete!",
        html: `
          <div>
            <p><strong>Orphaned data successfully cleaned up for:</strong></p>
            <p>Month: ${dayjs().month(monthIndex).year(yearNumber).format("MMMM YYYY")}</p>
            <br>
            <p><strong>Operations performed:</strong></p>
            <ul style="text-align: left; font-size: 12px; max-height: 300px; overflow-y: auto;">
              ${rollbackSteps.map(step => `<li>${step}</li>`).join('')}
            </ul>
          </div>
        `,
        width: 700,
      });
      
      setFullRollbackDialogOpen(false);
      setSelectedFullRollbackMonth("");
      setSelectedFullRollbackYear("");
      setFullRollbackDeleteYearlyGraph(false);
      setFullRollbackPreview({
        monthly_grpahs: 0,
        ledgers: 0,
        transection_requests: 0,
        monthly_closings: 0,
        usersToRestore: 0
      });
      setFullRollbackUserDetails([]);
      loadMonthlyClosures();
    } catch (error) {
      console.error("Error during full rollback:", error);
      Swal.fire({
        icon: "error",
        title: "Full Rollback Failed",
        text: `An error occurred during full rollback: ${error.message}`,
      });
    } finally {
      setFullRollbackProgress(0);
    }
  };

  const getStatusChip = (closure) => {
    if (!closure.user_id || !closure.closing_month || !closure.closing_year) {
      return <Chip label="Invalid Data" color="error" size="small" />;
    }
    return <Chip label="Ready for Rollback" color="success" size="small" />;
  };

  // Adicione esta função
  const handleRollbackOrphanEntries = async (monthOverride = null, yearOverride = null) => {
    const monthToUse = monthOverride || orphanMonth;
    const yearToUse = yearOverride || orphanYear;
    
    if (!monthToUse || !yearToUse) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please select both month and year",
      });
      return;
    }

    setLoading(true);
    try {
      const monthIndex = parseInt(monthToUse);
      const yearNumber = parseInt(yearToUse);
      
      console.log('🔍 DEBUG: Searching for orphan entries...');
      console.log(`   Month Index: ${monthIndex} (0-based, so month ${monthIndex + 1})`);
      console.log(`   Year: ${yearNumber}`);
      
      // Buscar monthly_grpahs que não têm monthly_closings correspondente
      const monthlyGraphsQuery = query(
        collection(DATABASE, "monthly_grpahs"),
        where("graph_month", "==", monthIndex),
        where("graph_year", "==", yearNumber)
      );
      const monthlyGraphsSnapshot = await getDocs(monthlyGraphsQuery);
      
      console.log(`   Found ${monthlyGraphsSnapshot.docs.length} monthly_grpahs entries`);
      
      const orphanGraphs = [];
      for (const graphDoc of monthlyGraphsSnapshot.docs) {
        const graphData = graphDoc.data();
        const userId = graphData.user_id;
        
        console.log(`   Checking user_id: ${userId} (doc_id: ${graphDoc.id})`);
        
        // Verificar se existe monthly_closing
        // IMPORTANTE: user_id pode ser string ou objeto com doc_id/id
        let userIdToSearch = userId;
        if (typeof userId === 'object' && userId !== null) {
          userIdToSearch = userId.doc_id || userId.id || userId;
        }
        
        const closingQuery = query(
          collection(DATABASE, "monthly_closings"),
          where("user_id", "==", userIdToSearch),
          where("closing_month", "==", monthIndex),
          where("closing_year", "==", yearNumber)
        );
        const closingSnapshot = await getDocs(closingQuery);
        
        console.log(`     monthly_closings found: ${closingSnapshot.docs.length}`);
        
        if (closingSnapshot.empty) {
          // Buscar informações do usuário para exibir
          let userEmail = "Unknown";
          let userName = "Unknown";
          let userType = null;
          try {
            // Tentar buscar por doc_id primeiro
            const userDocRef = doc(DATABASE, "users", userIdToSearch);
            const userDoc = await getDoc(userDocRef);
            if (userDoc.exists()) {
              const userData = userDoc.data();
              userEmail = userData.email || "Unknown";
              userName = userData.full_name || "Unknown";
              userType = userData.user_type !== undefined ? userData.user_type : null;
            } else {
              // Tentar buscar por query
              const userQuery = query(
                collection(DATABASE, "users"),
                where("doc_id", "==", userIdToSearch)
              );
              const userSnapshot = await getDocs(userQuery);
              if (!userSnapshot.empty) {
                const userData = userSnapshot.docs[0].data();
                userEmail = userData.email || "Unknown";
                userName = userData.full_name || "Unknown";
                userType = userData.user_type !== undefined ? userData.user_type : null;
              }
            }
          } catch (userError) {
            console.error(`Error fetching user data for ${userIdToSearch}:`, userError);
          }
          
          orphanGraphs.push({
            id: graphDoc.id,
            ...graphData,
            user_id: userIdToSearch,
            userEmail,
            userName,
            user_type: userType,
            profit_loss: graphData.profit_to_user || 0,
            current_balance: graphData.balance_after_commission || 0
          });
          console.log(`     ✅ Added orphan entry: ${userName} (${userEmail}), user_type: ${userType}, profit: $${(graphData.profit_to_user || 0).toFixed(2)}`);
        } else {
          console.log(`     ⏭️ Skipped (has monthly_closing)`);
        }
      }
      
      console.log(`✅ Total orphan entries found: ${orphanGraphs.length}`);
      
      // Count actual data that exists (ledgers, transactions, monthly_closings)
      let actualLedgersCount = 0;
      let actualTransactionsCount = 0;
      let actualMonthlyClosingsCount = 0;
      
      for (const graph of orphanGraphs) {
        const userId = graph.user_id;
        const reportingMonth = graph.graph_month === 11 ? 0 : graph.graph_month + 1;
        const reportingYear = graph.graph_month === 11 ? graph.graph_year + 1 : graph.graph_year;
        
        // Count ledgers
        try {
          const ledgersQuery = query(
            collection(DATABASE, "ledgers"),
            where("user_id", "==", userId),
            where("transection_type", "==", 3)
          );
          const ledgersSnapshot = await getDocs(ledgersQuery);
          ledgersSnapshot.docs.forEach(ledgerDoc => {
            const ledgerData = ledgerDoc.data();
            const ledgerDate = ledgerData.date?.toDate ? ledgerData.date.toDate() : new Date(ledgerData.date);
            if (ledgerDate.getMonth() === reportingMonth && 
                ledgerDate.getFullYear() === reportingYear && 
                ledgerDate.getDate() <= 15) {
              actualLedgersCount++;
            }
          });
        } catch (error) {
          console.error(`Error counting ledgers for ${userId}:`, error);
        }
        
        // Count transactions
        try {
          const transactionsQuery = query(
            collection(DATABASE, "transection_requests"),
            where("user_id", "==", userId),
            where("request_type", "==", 2)
          );
          const transactionsSnapshot = await getDocs(transactionsQuery);
          transactionsSnapshot.docs.forEach(transDoc => {
            const transData = transDoc.data();
            const transDate = transData.date?.toDate ? transData.date.toDate() : new Date(transData.date);
            if (transDate.getMonth() === reportingMonth && 
                transDate.getFullYear() === reportingYear && 
                transDate.getDate() <= 15) {
              actualTransactionsCount++;
            }
          });
        } catch (error) {
          console.error(`Error counting transactions for ${userId}:`, error);
        }
      }
      
      console.log(`📊 Actual data counts:`);
      console.log(`   Ledgers (type 3): ${actualLedgersCount}`);
      console.log(`   Transactions (type 2): ${actualTransactionsCount}`);
      console.log(`   Monthly closings: ${actualMonthlyClosingsCount} (should be 0 for orphans)`);
      
      setOrphanDataCounts({
        ledgers: actualLedgersCount,
        transactions: actualTransactionsCount,
        monthly_closings: actualMonthlyClosingsCount
      });
      
      setOrphanGraphs(orphanGraphs);
      setOrphanRollbackDialogOpen(true);
    } catch (error) {
      console.error("Error finding orphan entries:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: `Failed to find orphan entries: ${error.message}`,
      });
    } finally {
      setLoading(false);
    }
  };

  // Função para executar o rollback dos orphan entries
  const confirmOrphanRollback = async () => {
    setLoading(true);
    try {
      const batch = writeBatch(DATABASE);
      const rollbackSteps = [];
      
      for (const graph of orphanGraphs) {
        const userId = graph.user_id;
        
        // 1. Deletar monthly_grpahs
        batch.delete(doc(DATABASE, "monthly_grpahs", graph.id));
        rollbackSteps.push(`Deleted monthly graph: ${graph.id}`);
        
        // 2. Deletar ledgers
        const ledgersQuery = query(
          collection(DATABASE, "ledgers"),
          where("user_id", "==", userId),
          where("transection_type", "==", 3)
        );
        const ledgersSnapshot = await getDocs(ledgersQuery);
        const reportingMonth = graph.graph_month === 11 ? 0 : graph.graph_month + 1;
        const reportingYear = graph.graph_month === 11 ? graph.graph_year + 1 : graph.graph_year;
        
        ledgersSnapshot.docs.forEach(ledgerDoc => {
          const ledgerData = ledgerDoc.data();
          const ledgerDate = ledgerData.date?.toDate ? ledgerData.date.toDate() : new Date(ledgerData.date);
          if (ledgerDate.getMonth() === reportingMonth && 
              ledgerDate.getFullYear() === reportingYear && 
              ledgerDate.getDate() <= 15) {
            batch.delete(ledgerDoc.ref);
            rollbackSteps.push(`Deleted ledger: ${ledgerDoc.id}`);
          }
        });
        
        // 3. Deletar transactions
        const transactionsQuery = query(
          collection(DATABASE, "transection_requests"),
          where("user_id", "==", userId),
          where("request_type", "==", 2)
        );
        const transactionsSnapshot = await getDocs(transactionsQuery);
        
        transactionsSnapshot.docs.forEach(transDoc => {
          const transData = transDoc.data();
          const transDate = transData.date?.toDate ? transData.date.toDate() : new Date(transData.date);
          if (transDate.getMonth() === reportingMonth && 
              transDate.getFullYear() === reportingYear && 
              transDate.getDate() <= 15) {
            batch.delete(transDoc.ref);
            rollbackSteps.push(`Deleted transaction: ${transDoc.id}`);
          }
        });
        
        // 4. Restaurar net_balance - ONLY if we found ledgers or transactions to delete
        // This ensures we only restore balance if the closure actually created data
        const userHasLedgersOrTransactions = ledgersSnapshot.docs.some(ledgerDoc => {
          const ledgerData = ledgerDoc.data();
          const ledgerDate = ledgerData.date?.toDate ? ledgerData.date.toDate() : new Date(ledgerData.date);
          return ledgerDate.getMonth() === reportingMonth && 
                 ledgerDate.getFullYear() === reportingYear && 
                 ledgerDate.getDate() <= 15;
        }) || transactionsSnapshot.docs.some(transDoc => {
          const transData = transDoc.data();
          const transDate = transData.date?.toDate ? transData.date.toDate() : new Date(transData.date);
          return transDate.getMonth() === reportingMonth && 
                 transDate.getFullYear() === reportingYear && 
                 transDate.getDate() <= 15;
        });
        
        if (userHasLedgersOrTransactions) {
          try {
            const userDocRef = doc(DATABASE, "users", userId);
            const userDoc = await getDoc(userDocRef);
            if (userDoc.exists()) {
              const userData = userDoc.data();
              const currentBalance = userData.net_balance || 0;
              const profitLoss = parseFloat(graph.profit_to_user) || 0;
              const previousBalance = currentBalance - profitLoss;
              
              batch.update(userDocRef, {
                net_balance: previousBalance
              });
              rollbackSteps.push(`Restored balance for ${userId}: ${currentBalance} → ${previousBalance}`);
            }
          } catch (error) {
            console.error(`Error restoring balance for ${userId}:`, error);
            rollbackSteps.push(`⚠️ Skipped balance restoration for ${userId} due to error`);
          }
        } else {
          console.warn(`⚠️ Skipping balance restoration for ${userId} - no ledgers or transactions found to delete`);
          rollbackSteps.push(`⚠️ Skipped balance restoration for ${userId} - no ledgers/transactions found (closure may have failed before creating them)`);
        }
      }
      
      await batch.commit();
      
      Swal.fire({
        icon: "success",
        title: "Success",
        text: `Rolled back ${orphanGraphs.length} orphan entries`,
      });
      
      setOrphanRollbackDialogOpen(false);
      loadMonthlyClosures();
    } catch (error) {
      console.error("Error rolling back orphan entries:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to rollback orphan entries",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Monthly Closure Rollback Tool
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Safely rollback monthly closures and restore user balances to their previous state. 
        This tool will delete all related entries and restore the user's balance.
      </Typography>

      {/* Mode Toggle */}
      <Box sx={{ mb: 3 }}>
        <ToggleButtonGroup
          value={rollbackMode}
          exclusive
          onChange={(e, newMode) => {
            if (newMode !== null) {
              setRollbackMode(newMode);
            }
          }}
          aria-label="rollback mode"
        >
          <ToggleButton value="single" aria-label="single user">
            <PersonIcon sx={{ mr: 1 }} />
            Single User
          </ToggleButton>
          <ToggleButton value="batch" aria-label="batch rollback">
            <PeopleIcon sx={{ mr: 1 }} />
            Batch Rollback
          </ToggleButton>
          <ToggleButton value="full" aria-label="full rollback">
            <DeleteSweepIcon sx={{ mr: 1 }} />
            Full Rollback
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Batch Rollback Interface */}
      {rollbackMode === "batch" && (
        <Card sx={{ mb: 3, bgcolor: "info.main", color: "white" }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Batch Rollback Mode
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Roll back monthly closures for ALL users in a specific month.
            </Typography>
            
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ color: "white" }}>Month</InputLabel>
                  <Select
                    value={selectedBatchMonth}
                    onChange={(e) => setSelectedBatchMonth(e.target.value)}
                    label="Month"
                    sx={{ bgcolor: "white" }}
                  >
                    {Array.from({ length: 12 }, (_, i) => (
                      <MenuItem key={i} value={i}>
                        {dayjs().month(i).format("MMMM")}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ color: "white" }}>Year</InputLabel>
                  <Select
                    value={selectedBatchYear}
                    onChange={(e) => setSelectedBatchYear(e.target.value)}
                    label="Year"
                    sx={{ bgcolor: "white" }}
                  >
                    {Array.from({ length: 5 }, (_, i) => {
                      const year = new Date().getFullYear() - i;
                      return (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  color="warning"
                  fullWidth
                  onClick={handlePreviewBatchRollback}
                  disabled={!selectedBatchMonth || !selectedBatchYear || loading}
                  startIcon={<PeopleIcon />}
                >
                  Preview Users
                </Button>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={deleteYearlyGraph}
                      onChange={(e) => setDeleteYearlyGraph(e.target.checked)}
                      sx={{ color: "white", "&.Mui-checked": { color: "white" } }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        Delete yearly graph entry for this month
                      </Typography>
                      <Typography variant="caption">
                        ⚠️ Only check if rolling back ALL users for this month. 
                        Yearly graph is global and shared by all users.
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Full Rollback Interface */}
      {rollbackMode === "full" && (
        <Card sx={{ mb: 3, bgcolor: "warning.main", color: "white" }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Full Rollback Mode
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Rollback a complete monthly closure for a specific month. This mode automatically detects whether the month was closed successfully (has monthly_closings) or failed partway through (orphaned data only).
            </Typography>
            
            <Alert severity="info" sx={{ mb: 2, bgcolor: "rgba(0,0,0,0.2)", color: "white" }}>
              <Typography variant="body2">
                <strong>ℹ️ How it works:</strong> The system automatically checks if monthly_closings exist for the selected month.
                <br />• <strong>If monthly_closings exist:</strong> Performs a complete rollback of the successful closure (deletes monthly_grpahs, ledgers, transactions, and monthly_closings).
                <br />• <strong>If no monthly_closings:</strong> Cleans up orphaned data from a failed closure (monthly_grpahs, ledgers, transactions that were created but the closure didn't complete).
              </Typography>
            </Alert>
            
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ color: "white" }}>Month</InputLabel>
                  <Select
                    value={selectedFullRollbackMonth}
                    onChange={(e) => setSelectedFullRollbackMonth(e.target.value)}
                    label="Month"
                    sx={{ bgcolor: "white" }}
                  >
                    {Array.from({ length: 12 }, (_, i) => (
                      <MenuItem key={i} value={i}>
                        {dayjs().month(i).format("MMMM")}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ color: "white" }}>Year</InputLabel>
                  <Select
                    value={selectedFullRollbackYear}
                    onChange={(e) => setSelectedFullRollbackYear(e.target.value)}
                    label="Year"
                    sx={{ bgcolor: "white" }}
                  >
                    {Array.from({ length: 5 }, (_, i) => {
                      const year = new Date().getFullYear() - i;
                      return (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  color="error"
                  fullWidth
                  onClick={async () => {
                    if (!selectedFullRollbackMonth || !selectedFullRollbackYear) return;
                    
                    setLoading(true);
                    try {
                      // Check if there are monthly_closings for this month
                      const monthIndex = parseInt(selectedFullRollbackMonth);
                      const yearNumber = parseInt(selectedFullRollbackYear);
                      
                      const monthlyClosingsQuery = query(
                        collection(DATABASE, "monthly_closings"),
                        where("closing_month", "==", monthIndex),
                        where("closing_year", "==", yearNumber)
                      );
                      const monthlyClosingsSnapshot = await getDocs(monthlyClosingsQuery);
                      
                      if (monthlyClosingsSnapshot.size > 0) {
                        // Has monthly_closings - use full rollback (normal closure)
                        console.log(`✅ Found ${monthlyClosingsSnapshot.size} monthly_closings - using Full Rollback`);
                        await handlePreviewFullRollback();
                      } else {
                        // No monthly_closings - use orphan rollback (failed closure)
                        console.log(`⚠️ No monthly_closings found - using Orphan Rollback`);
                        await handleRollbackOrphanEntries(selectedFullRollbackMonth, selectedFullRollbackYear);
                      }
                    } catch (error) {
                      console.error("Error checking monthly_closings:", error);
                      Swal.fire({
                        icon: "error",
                        title: "Error",
                        text: `Failed to check monthly closures: ${error.message}`,
                      });
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={!selectedFullRollbackMonth || !selectedFullRollbackYear || loading}
                  startIcon={<DeleteSweepIcon />}
                >
                  Preview Rollback
                </Button>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={fullRollbackDeleteYearlyGraph}
                      onChange={(e) => setFullRollbackDeleteYearlyGraph(e.target.checked)}
                      sx={{ color: "white", "&.Mui-checked": { color: "white" } }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        Delete yearly graph entry for this month
                      </Typography>
                      <Typography variant="caption">
                        ⚠️ Only check if rolling back ALL users for this month. 
                        Yearly graph is global and shared by all users.
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                {monthlyClosures.length}
              </Typography>
              <Typography variant="body2">Total Closures</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success.main">
                {monthlyClosures.filter(c => c.user_id && c.closing_month !== undefined).length}
              </Typography>
              <Typography variant="body2">Valid Closures</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="warning.main">
                {monthlyClosures.filter(c => c.profit_loss < 0).length}
              </Typography>
              <Typography variant="body2">Loss Closures</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="error.main">
                {monthlyClosures.filter(c => !c.user_id || c.closing_month === undefined).length}
              </Typography>
              <Typography variant="body2">Invalid Closures</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <TextField
          label="Search by Email"
          value={searchEmail}
          onChange={(e) => setSearchEmail(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
        />
        <TextField
          label="Search by Month"
          value={searchMonth}
          onChange={(e) => setSearchMonth(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
        />
        <Button
          variant="outlined"
          startIcon={<SearchIcon />}
          onClick={loadMonthlyClosures}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Closures Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Month</TableCell>
              <TableCell>Profit/Loss</TableCell>
              <TableCell>Closing Balance</TableCell>
              <TableCell>Performance %</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredClosures.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No monthly closures found
                </TableCell>
              </TableRow>
            ) : (
              filteredClosures.map((closure) => (
                <TableRow key={closure.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {closure.userEmail}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {closure.userName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {closure.closing_month !== undefined && closure.closing_year !== undefined
                      ? dayjs().month(closure.closing_month).year(closure.closing_year).format("MMMM YYYY")
                      : "N/A"
                    }
                  </TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: closure.profit_loss >= 0 ? "#4caf50" : "#f44336",
                        fontWeight: "bold"
                      }}
                    >
                      ${closure.profit_loss?.toFixed(2) || "0.00"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      ${closure.closing_balance?.toFixed(2) || "0.00"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {closure.percent?.toFixed(2) || "0.00"}%
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {dayjs(closure.created_at).format("DD/MM/YYYY HH:mm")}
                  </TableCell>
                  <TableCell>{getStatusChip(closure)}</TableCell>
                  <TableCell>
                    <Tooltip title="Rollback this closure">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRollbackClosure(closure)}
                        disabled={!closure.user_id || closure.closing_month === undefined}
                      >
                        <UndoIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Rollback Confirmation Dialog */}
      <Dialog open={rollbackDialogOpen} onClose={() => setRollbackDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningIcon color="warning" />
            Confirm Monthly Closure Rollback
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedClosure && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  This action will permanently delete the monthly closure and restore the user's balance.
                </Typography>
              </Alert>
              
              <Typography variant="h6" gutterBottom>
                Closure Details:
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="body2"><strong>User:</strong> {selectedClosure.userEmail}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2"><strong>Month:</strong> {dayjs().month(selectedClosure.closing_month).year(selectedClosure.closing_year).format("MMMM YYYY")}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2"><strong>Current Balance:</strong> ${selectedClosure.closing_balance?.toFixed(2)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2"><strong>Profit/Loss:</strong> ${selectedClosure.profit_loss?.toFixed(2)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2"><strong>Performance:</strong> {selectedClosure.percent?.toFixed(2)}%</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2"><strong>Commission:</strong> ${selectedClosure.admin_comission?.toFixed(2)}</Typography>
                </Grid>
              </Grid>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>What will be restored:</strong><br/>
                  • User balance: ${(selectedClosure.closing_balance - selectedClosure.profit_loss).toFixed(2)}<br/>
                  • Monthly closure entry will be deleted<br/>
                  • Monthly graph data will be removed<br/>
                  • Performance ledgers and transactions will be deleted<br/>
                  • ⚠️ Yearly graph will NOT be deleted (global entry, shared by all users)
                </Typography>
              </Alert>

              {rollbackProgress > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    Rollback Progress: {rollbackProgress}%
                  </Typography>
                  <CircularProgress variant="determinate" value={rollbackProgress} />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRollbackDialogOpen(false)} disabled={rollbackProgress > 0}>
            Cancel
          </Button>
          <Button 
            onClick={confirmRollback} 
            color="error" 
            variant="contained"
            disabled={rollbackProgress > 0}
            startIcon={<UndoIcon />}
          >
            {rollbackProgress > 0 ? "Rolling Back..." : "Confirm Rollback"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Batch Rollback Dialog */}
      <Dialog open={batchDialogOpen} onClose={() => !batchProgress.total && setBatchDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningIcon color="warning" />
            Batch Rollback Confirmation
          </Box>
        </DialogTitle>
        <DialogContent>
          {batchPreviewUsers.length > 0 ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                ⚠️ This will rollback monthly closures for ALL {batchPreviewUsers.length} users below!
              </Typography>
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                ℹ️ No users have closures for this month, but you can still delete the yearly graph entry.
              </Typography>
            </Alert>
          )}
          
          {batchPreviewUsers.length > 0 ? (
            <Box>
              <Typography variant="h6" gutterBottom>
                Users to be rolled back:
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Month: {selectedBatchMonth !== "" && dayjs().month(parseInt(selectedBatchMonth)).format("MMMM")} {selectedBatchYear}
              </Typography>
              
              <TableContainer component={Paper} sx={{ maxHeight: 300, mb: 2 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Email</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Profit/Loss</TableCell>
                      <TableCell>Balance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {batchPreviewUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>{user.userEmail}</TableCell>
                        <TableCell>{user.userName}</TableCell>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: user.profit_loss >= 0 ? "#4caf50" : "#f44336",
                              fontWeight: "bold"
                            }}
                          >
                            ${user.profit_loss?.toFixed(2) || "0.00"}
                          </Typography>
                        </TableCell>
                        <TableCell>${user.closing_balance?.toFixed(2) || "0.00"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Summary Box with Totals - Separated by User Type */}
              {(() => {
                const formatCurrency = (amount) => {
                  return new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(amount);
                };

                // Separate users by type
                const individualUsers = batchPreviewUsers.filter(user => user.userType === 0 || user.userType === null || user.userType === undefined);
                const corporateUsers = batchPreviewUsers.filter(user => user.userType === 1);

                // Calculate totals for Individual Customers
                const individualProfitLoss = individualUsers.reduce((sum, user) => sum + (parseFloat(user.profit_loss) || 0), 0);
                const individualBalance = individualUsers.reduce((sum, user) => sum + (parseFloat(user.closing_balance) || 0), 0);
                const individualCount = individualUsers.length;

                // Calculate totals for Corporate Customers
                const corporateProfitLoss = corporateUsers.reduce((sum, user) => sum + (parseFloat(user.profit_loss) || 0), 0);
                const corporateBalance = corporateUsers.reduce((sum, user) => sum + (parseFloat(user.closing_balance) || 0), 0);
                const corporateCount = corporateUsers.length;

                // Calculate grand totals
                const totalProfitLoss = individualProfitLoss + corporateProfitLoss;
                const totalBalance = individualBalance + corporateBalance;
                const totalUsers = batchPreviewUsers.length;

                return (
                  <Box sx={{ mb: 2 }}>
                    {/* Individual Customers Summary */}
                    {individualCount > 0 && (
                      <Box sx={{ 
                        p: 2, 
                        backgroundColor: "rgba(42, 245, 152, 0.1)", 
                        borderRadius: 2,
                        border: "1px solid rgba(42, 245, 152, 0.3)",
                        mb: 2
                      }}>
                        <Typography variant="body2" sx={{ mb: 1, color: "#fff", fontWeight: 600, display: "flex", alignItems: "center", gap: 1 }}>
                          <PersonIcon sx={{ fontSize: 18 }} />
                          Individual Customers Summary
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          <Box>
                            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", display: "block" }}>
                              Total Profit/Loss:
                            </Typography>
                            <Typography variant="h6" sx={{ color: individualProfitLoss >= 0 ? "#2af598" : "#f52a2d", fontWeight: 700 }}>
                              {formatCurrency(individualProfitLoss)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", display: "block" }}>
                              Total Balance:
                            </Typography>
                            <Typography variant="h6" sx={{ color: "#2af598", fontWeight: 700 }}>
                              {formatCurrency(individualBalance)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", display: "block" }}>
                              Total Users:
                            </Typography>
                            <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700 }}>
                              {individualCount}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}

                    {/* Corporate Customers Summary */}
                    {corporateCount > 0 && (
                      <Box sx={{ 
                        p: 2, 
                        backgroundColor: "rgba(66, 165, 245, 0.1)", 
                        borderRadius: 2,
                        border: "1px solid rgba(66, 165, 245, 0.3)",
                        mb: 2
                      }}>
                        <Typography variant="body2" sx={{ mb: 1, color: "#fff", fontWeight: 600, display: "flex", alignItems: "center", gap: 1 }}>
                          <PeopleIcon sx={{ fontSize: 18 }} />
                          Corporate Customers Summary
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          <Box>
                            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", display: "block" }}>
                              Total Profit/Loss:
                            </Typography>
                            <Typography variant="h6" sx={{ color: corporateProfitLoss >= 0 ? "#42a5f5" : "#f52a2d", fontWeight: 700 }}>
                              {formatCurrency(corporateProfitLoss)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", display: "block" }}>
                              Total Balance:
                            </Typography>
                            <Typography variant="h6" sx={{ color: "#42a5f5", fontWeight: 700 }}>
                              {formatCurrency(corporateBalance)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", display: "block" }}>
                              Total Users:
                            </Typography>
                            <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700 }}>
                              {corporateCount}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}

                    {/* Grand Total Summary */}
                    <Box sx={{ 
                      p: 2, 
                      backgroundColor: "rgba(156, 39, 176, 0.1)", 
                      borderRadius: 2,
                      border: "1px solid rgba(156, 39, 176, 0.3)"
                    }}>
                      <Typography variant="body2" sx={{ mb: 1, color: "#fff", fontWeight: 600 }}>
                        Grand Total Summary
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <Box>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", display: "block" }}>
                            Total Profit/Loss:
                          </Typography>
                          <Typography variant="h6" sx={{ color: totalProfitLoss >= 0 ? "#9c27b0" : "#f52a2d", fontWeight: 700 }}>
                            {formatCurrency(totalProfitLoss)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", display: "block" }}>
                            Total Balance:
                          </Typography>
                          <Typography variant="h6" sx={{ color: "#9c27b0", fontWeight: 700 }}>
                            {formatCurrency(totalBalance)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", display: "block" }}>
                            Total Users:
                          </Typography>
                          <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700 }}>
                            {totalUsers}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })()}

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Operations for EACH user:</strong><br/>
                  • Delete monthly closure entry<br/>
                  • Delete monthly graph data<br/>
                  • Delete performance ledgers<br/>
                  • Delete performance transactions<br/>
                  • Restore user balance to previous month<br/>
                  {deleteYearlyGraph && (
                    <>
                      <br/>
                      <strong style={{ color: "#d32f2f" }}>⚠️ ADDITIONALLY (because checkbox is checked):</strong><br/>
                      <span style={{ color: "#d32f2f" }}>• Delete global yearly_graph entry for {dayjs().month(parseInt(selectedBatchMonth)).format("MMMM")} {selectedBatchYear}</span><br/>
                      <span style={{ color: "#d32f2f" }}>• This affects ALL users' yearly graph display!</span>
                    </>
                  )}
                </Typography>
              </Alert>
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Month: {selectedBatchMonth !== "" && dayjs().month(parseInt(selectedBatchMonth)).format("MMMM")} {selectedBatchYear}
              </Typography>
              
              {deleteYearlyGraph ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>⚠️ Yearly Graph Deletion Only:</strong><br/>
                    • Delete global yearly_graph entry for {dayjs().month(parseInt(selectedBatchMonth)).format("MMMM")} {selectedBatchYear}<br/>
                    • This affects ALL users' yearly graph display!<br/>
                    • No user rollbacks will be performed (0 users)
                  </Typography>
                </Alert>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>ℹ️ No Operations to Perform:</strong><br/>
                    • No users have closures for this month<br/>
                    • Check the "Delete yearly graph entry" checkbox if you want to clean up the yearly graph
                  </Typography>
                </Alert>
              )}
            </Box>
          )}

          {batchProgress.total > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                Progress: {batchProgress.current} / {batchProgress.total} users
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={(batchProgress.current / batchProgress.total) * 100} 
              />
              {batchProgress.current > 0 && batchProgress.current < batchProgress.total && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  Processing {batchPreviewUsers[batchProgress.current - 1]?.userEmail}...
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setBatchDialogOpen(false);
              setBatchPreviewUsers([]);
              setBatchProgress({ current: 0, total: 0 });
            }} 
            disabled={batchProgress.total > 0}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmBatchRollback} 
            color="error" 
            variant="contained"
            disabled={batchProgress.total > 0 || (batchPreviewUsers.length === 0 && !deleteYearlyGraph)}
            startIcon={<UndoIcon />}
          >
            {batchProgress.total > 0 
              ? "Processing..." 
              : batchPreviewUsers.length > 0 
                ? `Confirm Batch Rollback (${batchPreviewUsers.length} users)`
                : deleteYearlyGraph
                  ? "Delete Yearly Graph Only"
                  : "Nothing to Rollback"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Full Rollback Dialog */}
      <Dialog open={fullRollbackDialogOpen} onClose={() => fullRollbackProgress === 0 && setFullRollbackDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DeleteSweepIcon color="error" />
            Full Rollback Confirmation - Orphaned Data Cleanup
          </Box>
        </DialogTitle>
        <DialogContent>
          {fullRollbackPreview.monthly_grpahs > 0 || fullRollbackPreview.ledgers > 0 || 
           fullRollbackPreview.transection_requests > 0 || fullRollbackPreview.monthly_closings > 0 ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                ⚠️ This will clean up ALL orphaned data for {selectedFullRollbackMonth !== "" && dayjs().month(parseInt(selectedFullRollbackMonth)).format("MMMM")} {selectedFullRollbackYear}!
              </Typography>
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                ℹ️ No orphaned data found for this month, but you can still delete the yearly graph entry.
              </Typography>
            </Alert>
          )}
          
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Month: {selectedFullRollbackMonth !== "" && dayjs().month(parseInt(selectedFullRollbackMonth)).format("MMMM")} {selectedFullRollbackYear}
          </Typography>
          
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Orphaned Data Found:
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h4" color="primary">
                      {fullRollbackPreview.monthly_grpahs}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      monthly_grpahs entries
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h4" color="primary">
                      {fullRollbackPreview.ledgers}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ledger entries (type 3)
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h4" color="primary">
                      {fullRollbackPreview.transection_requests}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      transection_requests (type 2)
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h4" color="primary">
                      {fullRollbackPreview.monthly_closings}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      monthly_closings entries
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ bgcolor: "warning.light" }}>
                  <CardContent>
                    <Typography variant="h4" color="error">
                      {fullRollbackPreview.usersToRestore}
                    </Typography>
                    <Typography variant="body2">
                      Users whose balances will be restored
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>

          {/* Detailed User List */}
          {fullRollbackUserDetails.length > 0 && (
            <Box sx={{ mt: 3, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Users Affected ({fullRollbackUserDetails.length}):
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 400, mt: 2 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>User</strong></TableCell>
                      <TableCell><strong>Email</strong></TableCell>
                      <TableCell align="center"><strong>Type</strong></TableCell>
                      <TableCell align="right"><strong>Performance %</strong></TableCell>
                      <TableCell align="right"><strong>Profit/Loss</strong></TableCell>
                      <TableCell align="right"><strong>Closing Balance</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fullRollbackUserDetails.map((user, index) => (
                      <TableRow key={user.userId} hover>
                        <TableCell>{user.userName}</TableCell>
                        <TableCell>{user.userEmail}</TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={user.userType === 0 ? "Individual" : user.userType === 1 ? "Corporate" : "Unknown"}
                            color={user.userType === 0 ? "primary" : user.userType === 1 ? "secondary" : "default"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          {user.performancePercent.toFixed(2)}%
                        </TableCell>
                        <TableCell align="right">
                          <Typography 
                            color={user.profitLoss >= 0 ? "success.main" : "error.main"}
                            fontWeight="bold"
                          >
                            ${user.profitLoss.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          ${user.closingBalance.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Totals Row */}
                    <TableRow sx={{ bgcolor: "grey.100", fontWeight: "bold" }}>
                      <TableCell colSpan={3} align="right">
                        <strong>TOTALS:</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>
                          {fullRollbackUserDetails.length > 0
                            ? (fullRollbackUserDetails.reduce((sum, u) => sum + u.performancePercent, 0) / fullRollbackUserDetails.length).toFixed(2)
                            : "0.00"}%
                        </strong>
                      </TableCell>
                      <TableCell align="right">
                        <Typography color="text.primary" fontWeight="bold">
                          <strong>${fullRollbackUserDetails.reduce((sum, u) => sum + u.profitLoss, 0).toFixed(2)}</strong>
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <strong>${fullRollbackUserDetails.reduce((sum, u) => sum + u.closingBalance, 0).toFixed(2)}</strong>
                      </TableCell>
                    </TableRow>
                    {/* Individual Totals */}
                    {fullRollbackUserDetails.filter(u => u.userType === 0).length > 0 && (
                      <TableRow sx={{ bgcolor: "primary.light", color: "white" }}>
                        <TableCell colSpan={3} align="right">
                          <strong>Individual Subtotal ({fullRollbackUserDetails.filter(u => u.userType === 0).length} users):</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>
                            {(() => {
                              const individualUsers = fullRollbackUserDetails.filter(u => u.userType === 0);
                              return individualUsers.length > 0
                                ? (individualUsers.reduce((sum, u) => sum + u.performancePercent, 0) / individualUsers.length).toFixed(2)
                                : "0.00";
                            })()}%
                          </strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>
                            ${fullRollbackUserDetails.filter(u => u.userType === 0).reduce((sum, u) => sum + u.profitLoss, 0).toFixed(2)}
                          </strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>
                            ${fullRollbackUserDetails.filter(u => u.userType === 0).reduce((sum, u) => sum + u.closingBalance, 0).toFixed(2)}
                          </strong>
                        </TableCell>
                      </TableRow>
                    )}
                    {/* Corporate Totals */}
                    {fullRollbackUserDetails.filter(u => u.userType === 1).length > 0 && (
                      <TableRow sx={{ bgcolor: "secondary.light", color: "white" }}>
                        <TableCell colSpan={3} align="right">
                          <strong>Corporate Subtotal ({fullRollbackUserDetails.filter(u => u.userType === 1).length} users):</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>
                            {(() => {
                              const corporateUsers = fullRollbackUserDetails.filter(u => u.userType === 1);
                              return corporateUsers.length > 0
                                ? (corporateUsers.reduce((sum, u) => sum + u.performancePercent, 0) / corporateUsers.length).toFixed(2)
                                : "0.00";
                            })()}%
                          </strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>
                            ${fullRollbackUserDetails.filter(u => u.userType === 1).reduce((sum, u) => sum + u.profitLoss, 0).toFixed(2)}
                          </strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>
                            ${fullRollbackUserDetails.filter(u => u.userType === 1).reduce((sum, u) => sum + u.closingBalance, 0).toFixed(2)}
                          </strong>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Operations that will be performed:</strong><br/>
              • Delete {fullRollbackPreview.monthly_grpahs} monthly_grpahs entries<br/>
              • Delete {fullRollbackPreview.ledgers} ledger entries (performance type)<br/>
              • Delete {fullRollbackPreview.transection_requests} transection_requests (performance type)<br/>
              • Delete {fullRollbackPreview.monthly_closings} monthly_closings entries (if any)<br/>
              • Restore {fullRollbackPreview.usersToRestore} user balances<br/>
              {fullRollbackDeleteYearlyGraph && (
                <>
                  <br/>
                  <strong style={{ color: "#d32f2f" }}>⚠️ ADDITIONALLY (because checkbox is checked):</strong><br/>
                  <span style={{ color: "#d32f2f" }}>• Delete global yearly_graph entry for {selectedFullRollbackMonth !== "" && dayjs().month(parseInt(selectedFullRollbackMonth)).format("MMMM")} {selectedFullRollbackYear}</span><br/>
                  <span style={{ color: "#d32f2f" }}>• This affects ALL users' yearly graph display!</span>
                </>
              )}
            </Typography>
          </Alert>

          {fullRollbackProgress > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                Progress: {fullRollbackProgress}%
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={fullRollbackProgress} 
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setFullRollbackDialogOpen(false);
              setFullRollbackPreview({
                monthly_grpahs: 0,
                ledgers: 0,
                transection_requests: 0,
                monthly_closings: 0,
                usersToRestore: 0
              });
              setFullRollbackUserDetails([]);
              setFullRollbackProgress(0);
            }} 
            disabled={fullRollbackProgress > 0}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmFullRollback} 
            color="error" 
            variant="contained"
            disabled={fullRollbackProgress > 0 || 
              (fullRollbackPreview.monthly_grpahs === 0 && 
               fullRollbackPreview.ledgers === 0 && 
               fullRollbackPreview.transection_requests === 0 && 
               fullRollbackPreview.monthly_closings === 0 && 
               !fullRollbackDeleteYearlyGraph)}
            startIcon={<DeleteSweepIcon />}
          >
            {fullRollbackProgress > 0 
              ? "Processing..." 
              : "Execute Full Rollback"
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Orphan Rollback Dialog */}
      <Dialog open={orphanRollbackDialogOpen} onClose={() => setOrphanRollbackDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningIcon color="warning" />
            Orphan Rollback Confirmation
    </Box>
        </DialogTitle>
        <DialogContent>
          {orphanGraphs.length > 0 ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                ⚠️ This will rollback orphaned data for ALL {orphanGraphs.length} users below!
              </Typography>
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                ℹ️ No orphaned data found for this month, but you can still delete the yearly graph entry.
              </Typography>
            </Alert>
          )}
          
          {orphanGraphs.length > 0 ? (
            <Box>
              <Typography variant="h6" gutterBottom>
                Users to be rolled back:
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Month: {orphanMonth} {orphanYear}
              </Typography>
              
              <TableContainer component={Paper} sx={{ maxHeight: 300, mb: 2 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Month</TableCell>
                      <TableCell>Profit/Loss</TableCell>
                      <TableCell>Balance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orphanGraphs.map((graph) => (
                      <TableRow key={graph.id}>
                        <TableCell>{graph.userEmail}</TableCell>
                        <TableCell>{graph.graph_month !== undefined && graph.graph_year !== undefined
                          ? dayjs().month(graph.graph_month).year(graph.graph_year).format("MMMM YYYY")
                          : "N/A"
                        }</TableCell>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: graph.profit_loss >= 0 ? "#4caf50" : "#f44336",
                              fontWeight: "bold"
                            }}
                          >
                            ${graph.profit_loss?.toFixed(2) || "0.00"}
                          </Typography>
                        </TableCell>
                        <TableCell>${graph.current_balance?.toFixed(2) || "0.00"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Summary Box with Totals - Separated by User Type */}
              {(() => {
                const formatCurrency = (amount) => {
                  return new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(amount);
                };

                // Separate users by type
                const individualUsers = orphanGraphs.filter(graph => graph.user_type === 0 || graph.user_type === null || graph.user_type === undefined);
                const corporateUsers = orphanGraphs.filter(graph => graph.user_type === 1);
                
                // DEBUG: Log user type distribution
                console.log('📊 DEBUG: User type distribution in orphan rollback:');
                console.log(`   Individual (type 0/null/undefined): ${individualUsers.length}`);
                console.log(`   Corporate (type 1): ${corporateUsers.length}`);
                console.log(`   Users with undefined user_type: ${orphanGraphs.filter(g => g.user_type === undefined).length}`);
                console.log(`   Users with null user_type: ${orphanGraphs.filter(g => g.user_type === null).length}`);
                console.log(`   Users with type 0: ${orphanGraphs.filter(g => g.user_type === 0).length}`);
                console.log(`   Users with type 1: ${orphanGraphs.filter(g => g.user_type === 1).length}`);

                // Calculate totals for Individual Customers
                const individualProfitLoss = individualUsers.reduce((sum, graph) => sum + (parseFloat(graph.profit_loss) || 0), 0);
                const individualBalance = individualUsers.reduce((sum, graph) => sum + (parseFloat(graph.current_balance) || 0), 0);
                const individualCount = individualUsers.length;

                // Calculate totals for Corporate Customers
                const corporateProfitLoss = corporateUsers.reduce((sum, graph) => sum + (parseFloat(graph.profit_loss) || 0), 0);
                const corporateBalance = corporateUsers.reduce((sum, graph) => sum + (parseFloat(graph.current_balance) || 0), 0);
                const corporateCount = corporateUsers.length;
                
                // DEBUG: Log totals
                console.log('📊 DEBUG: Profit/Loss totals:');
                console.log(`   Individual: $${individualProfitLoss.toFixed(2)} (${individualCount} users)`);
                console.log(`   Corporate: $${corporateProfitLoss.toFixed(2)} (${corporateCount} users)`);
                console.log(`   Grand Total: $${(individualProfitLoss + corporateProfitLoss).toFixed(2)}`);

                // Calculate grand totals
                const totalProfitLoss = individualProfitLoss + corporateProfitLoss;
                const totalBalance = individualBalance + corporateBalance;
                const totalUsers = orphanGraphs.length;

                return (
                  <Box sx={{ mb: 2 }}>
                    {/* Individual Customers Summary */}
                    {individualCount > 0 && (
                      <Box sx={{ 
                        p: 2, 
                        backgroundColor: "rgba(42, 245, 152, 0.1)", 
                        borderRadius: 2,
                        border: "1px solid rgba(42, 245, 152, 0.3)",
                        mb: 2
                      }}>
                        <Typography variant="body2" sx={{ mb: 1, color: "#fff", fontWeight: 600, display: "flex", alignItems: "center", gap: 1 }}>
                          <PersonIcon sx={{ fontSize: 18 }} />
                          Individual Customers Summary
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          <Box>
                            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", display: "block" }}>
                              Total Profit/Loss:
                            </Typography>
                            <Typography variant="h6" sx={{ color: individualProfitLoss >= 0 ? "#2af598" : "#f52a2d", fontWeight: 700 }}>
                              {formatCurrency(individualProfitLoss)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", display: "block" }}>
                              Total Balance:
                            </Typography>
                            <Typography variant="h6" sx={{ color: "#2af598", fontWeight: 700 }}>
                              {formatCurrency(individualBalance)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", display: "block" }}>
                              Total Users:
                            </Typography>
                            <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700 }}>
                              {individualCount}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}

                    {/* Corporate Customers Summary */}
                    {corporateCount > 0 && (
                      <Box sx={{ 
                        p: 2, 
                        backgroundColor: "rgba(66, 165, 245, 0.1)", 
                        borderRadius: 2,
                        border: "1px solid rgba(66, 165, 245, 0.3)",
                        mb: 2
                      }}>
                        <Typography variant="body2" sx={{ mb: 1, color: "#fff", fontWeight: 600, display: "flex", alignItems: "center", gap: 1 }}>
                          <PeopleIcon sx={{ fontSize: 18 }} />
                          Corporate Customers Summary
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          <Box>
                            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", display: "block" }}>
                              Total Profit/Loss:
                            </Typography>
                            <Typography variant="h6" sx={{ color: corporateProfitLoss >= 0 ? "#42a5f5" : "#f52a2d", fontWeight: 700 }}>
                              {formatCurrency(corporateProfitLoss)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", display: "block" }}>
                              Total Balance:
                            </Typography>
                            <Typography variant="h6" sx={{ color: "#42a5f5", fontWeight: 700 }}>
                              {formatCurrency(corporateBalance)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", display: "block" }}>
                              Total Users:
                            </Typography>
                            <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700 }}>
                              {corporateCount}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}

                    {/* Grand Total Summary */}
                    <Box sx={{ 
                      p: 2, 
                      backgroundColor: "rgba(156, 39, 176, 0.1)", 
                      borderRadius: 2,
                      border: "1px solid rgba(156, 39, 176, 0.3)"
                    }}>
                      <Typography variant="body2" sx={{ mb: 1, color: "#fff", fontWeight: 600 }}>
                        Grand Total Summary
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <Box>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", display: "block" }}>
                            Total Profit/Loss:
                          </Typography>
                          <Typography variant="h6" sx={{ color: totalProfitLoss >= 0 ? "#9c27b0" : "#f52a2d", fontWeight: 700 }}>
                            {formatCurrency(totalProfitLoss)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", display: "block" }}>
                            Total Balance:
                          </Typography>
                          <Typography variant="h6" sx={{ color: "#9c27b0", fontWeight: 700 }}>
                            {formatCurrency(totalBalance)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", display: "block" }}>
                            Total Users:
                          </Typography>
                          <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700 }}>
                            {totalUsers}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })()}

              {/* Actual Data Counts */}
              <Alert severity={orphanDataCounts.ledgers === 0 && orphanDataCounts.transactions === 0 ? "warning" : "info"} sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  ⚠️ Actual Data Found (will be deleted):
                </Typography>
                <Typography variant="body2">
                  • {orphanGraphs.length} monthly_grpahs entries<br/>
                  • {orphanDataCounts.ledgers} ledger entries (type 3) {orphanDataCounts.ledgers === 0 && <span style={{ color: "#ff9800" }}>(⚠️ No ledgers found - closure may have failed before creating them)</span>}<br/>
                  • {orphanDataCounts.transactions} transection_requests (type 2) {orphanDataCounts.transactions === 0 && <span style={{ color: "#ff9800" }}>(⚠️ No transactions found - closure may have failed before creating them)</span>}<br/>
                  • {orphanDataCounts.monthly_closings} monthly_closings entries (should be 0 for orphans)<br/>
                  • {orphanDataCounts.ledgers === 0 && orphanDataCounts.transactions === 0 ? (
                    <span style={{ color: "#4caf50" }}>0 user balances will be restored (✅ SAFE: No ledgers/transactions found, so balance was never updated - nothing to restore)</span>
                  ) : (
                    <span>{orphanGraphs.length} user balances will be restored</span>
                  )}
                </Typography>
              </Alert>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Operations for EACH user:</strong><br/>
                  • Delete monthly_grpahs entry<br/>
                  • Delete ledger entries (performance type) - {orphanDataCounts.ledgers > 0 ? `${orphanDataCounts.ledgers} found` : 'NONE FOUND'}<br/>
                  • Delete transaction entries (performance type) - {orphanDataCounts.transactions > 0 ? `${orphanDataCounts.transactions} found` : 'NONE FOUND'}<br/>
                  • Restore net_balance to previous month - {orphanDataCounts.ledgers === 0 && orphanDataCounts.transactions === 0 ? (
                    <span style={{ color: "#4caf50" }}>SKIPPED (✅ SAFE: No ledgers/transactions found, so balance was never updated during closure)</span>
                  ) : (
                    <span>WILL BE RESTORED</span>
                  )}<br/>
                  {orphanDeleteYearlyGraph && (
                    <>
                      <br/>
                      <strong style={{ color: "#d32f2f" }}>⚠️ ADDITIONALLY (because checkbox is checked):</strong><br/>
                      <span style={{ color: "#d32f2f" }}>• Delete global yearly_graph entry for {dayjs().month(parseInt(orphanMonth)).format("MMMM")} {orphanYear}</span><br/>
                      <span style={{ color: "#d32f2f" }}>• This affects ALL users' yearly graph display!</span>
                    </>
                  )}
                </Typography>
              </Alert>
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Month: {orphanMonth} {orphanYear}
              </Typography>
              
              {orphanDeleteYearlyGraph ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>⚠️ Yearly Graph Deletion Only:</strong><br/>
                    • Delete global yearly_graph entry for {dayjs().month(parseInt(orphanMonth)).format("MMMM")} {orphanYear}<br/>
                    • This affects ALL users' yearly graph display!<br/>
                    • No user rollbacks will be performed (0 users)
                  </Typography>
                </Alert>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>ℹ️ No Operations to Perform:</strong><br/>
                    • No orphaned data found for this month<br/>
                    • Check the "Delete yearly graph entry" checkbox if you want to clean up the yearly graph
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setOrphanRollbackDialogOpen(false);
              setOrphanGraphs([]);
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmOrphanRollback}
            variant="contained"
            color="error"
            disabled={loading || orphanGraphs.length === 0}
            startIcon={loading ? <CircularProgress size={20} /> : <DeleteSweepIcon />}
          >
            {loading ? "Rolling Back..." : "Confirm Orphan Rollback"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MonthlyClosureRollback;
